import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
    Link,
    useNavigate,
    useRouter,
    useSearch,
} from '@tanstack/react-router';
import {
    Activity,
    AlertCircle,
    Building,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    CircleDot,
    Clock,
    ExternalLink,
    LayoutGrid,
    Loader2,
    MessageSquareText,
    MoreHorizontal,
    Plus,
    RefreshCw,
    Send,
    Tag,
    TestTube2,
    Users,
    X,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, {
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';

import { BlockedPhonePopover } from '@apps/web/components/caregivers/blocked_phone_popover';
import { ShiftCaregiverControl } from '@apps/web/components/dashboard/shift_caregiver_control';
import {
    type ShiftFilterClient,
    useShiftClientFilterConfig,
} from '@apps/web/components/dashboard/shift_client_filter';
import {
    isRangeEntirelyInPast,
    isTodayRange,
    isValidDateKey,
    parseShiftRange,
    resolveInitialShiftRange,
    serializeShiftRange,
    shiftRangeStorageKey,
} from '@apps/web/components/dashboard/shift_date_range';
import { ShiftDetailsHeaderActions } from '@apps/web/components/dashboard/shift_details_header_actions';
import {
    ALL_UNIFIED_STATUS_VALUES,
    expandCoverageFilterValue,
    expandCoverageStatuses,
    isEhrPendingConfirmation,
    isRouteShiftStatus,
    resolveShiftStatus,
    type RouteShiftStatus,
    statusFilterOptions,
    type UnifiedShiftStatus,
} from '@apps/web/components/dashboard/shift_list_filters';
import { useShiftListRouteFilters } from '@apps/web/components/dashboard/use_shift_list_route_filters';
import { EntityEventsTimeline } from '@apps/web/components/events/entity_events_timeline';
import { CreateOutreachModal } from '@apps/web/components/outreaches/create_outreach_modal';
import { multiShiftOutreachMax } from '@apps/web/components/outreaches/edit_shifts_section';
import { isActiveOutreach } from '@apps/web/components/outreaches/utils';
import { TrainingModePageTourReplayButton } from '@apps/web/components/page_tour/training_mode_page_tour';
import { SandboxSetupModal } from '@apps/web/components/sandbox_setup_modal';
import { MessagesPane } from '@apps/web/components/shared/messages/messages_pane';
import { PendingMessagesDialog } from '@apps/web/components/shared/messages/messages_pending_dialog';
import { CreateShiftModal } from '@apps/web/components/shifts/create_shift_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import {
    type ColumnFiltersState,
    DataTable,
    DataTableColumnHeader,
} from '@apps/web/components/ui/data_table';
import {
    type CustomFilterConfig,
    DataTableToolbar,
} from '@apps/web/components/ui/data_table_toolbar';
import {
    DateRangePicker,
    type DateRangePickerValue,
} from '@apps/web/components/ui/date_range_picker';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { EmptyState } from '@apps/web/components/ui/empty_state';
import { Markdown } from '@apps/web/components/ui/markdown';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@apps/web/components/ui/sheet';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    type StatusOption,
    StatusPicker,
} from '@apps/web/components/ui/status_picker';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    useOrganizationPendingMessagesCountRealtime,
    useShiftsRealtime,
} from '@apps/web/hooks/realtime_hooks';
import { formatCoordinatorName } from '@apps/web/hooks/use_care_coordinator_filter';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useResourceOrganizationLookup } from '@apps/web/hooks/use_resource_organization_lookup';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    compareCaregiverDisplayName,
    formatCaregiverName,
} from '@apps/web/lib/caregiver_name';
import {
    generateExternalClientUrl,
    getDataSourceDisplayName,
} from '@apps/web/lib/data_source_utils';
import {
    dateKeyToEndUtcIso,
    dateKeyToStartUtcIso,
    formatDateKey,
    formatDateKeyLong,
    formatDateKeyRange,
    formatDayOfWeek,
    formatFullDate,
    formatRelativeTime,
    formatTime,
    toDateKey,
    WEEKDAY_INDEX_BY_DAY,
    type WeekdayIndex,
} from '@apps/web/lib/date_utils';
import { isEhrCaregiverUnassignmentWritebackEnabled } from '@apps/web/lib/ehr_caregiver_unassignment_writeback';
import { showCaregiverWritebackToast } from '@apps/web/lib/ehr_shift_writeback_feedback';
import { EVENTS } from '@apps/web/lib/events';
import {
    formatExternalStatus,
    formatOfficeName,
} from '@apps/web/lib/format_utils';
import { useMode } from '@apps/web/lib/mode_store';
import { type RowNavigation } from '@apps/web/lib/navigation_utils';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { runSyncWithFeedback } from '@apps/web/lib/sync_feedback';
import { type ColumnDef, inMultiSelect, type Table } from '@apps/web/lib/table';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import {
    type BlockedCaregiverInfo,
    type OutreachType,
    useBlockedCaregivers,
    useClientCareCoordinators,
    useLatestShiftsSync,
    useMyCareCoordinator,
    useOrganization,
    useOrganizationCareCoordinators,
    useOrganizationCaregivers,
    useOrganizationClients,
    useOrganizationClientTags,
    useOrganizationDataSourceAccounts,
    useOutreachesBatch,
    useShift,
    useShiftSearch,
    useShiftSearchFilterValues,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type CareCoordinatorRead,
    type components,
    type DataSourceType,
    type OrganizationDataSourceAccount,
    type Outreach,
    type Shift,
    type ShiftStage,
    type ShiftStatus,
} from '@libraries/typescript/api_client';

// Special ID for filtering by unassigned (no care coordinator)
const UNASSIGNED_CARE_COORDINATOR_ID = '__unassigned__';

const SHIFT_UNASSIGNMENT_WRITEBACK_SOURCES: ReadonlySet<DataSourceType> =
    new Set(['axiscare', 'generations_homecare', 'hhaexchange', 'wellsky']);

function formatClientReferenceTerm(referenceTerm: string): string {
    return `${referenceTerm.charAt(0).toUpperCase()}${referenceTerm.slice(1)}`;
}

type ShiftCalloutStatus = {
    readonly hasActiveCallout: boolean;
    readonly activeCallout: Outreach | null;
    readonly activeClockReminder: Outreach | null;
};

const EMPTY_SHIFT_CALLOUT_STATUS: ShiftCalloutStatus = {
    hasActiveCallout: false,
    activeCallout: null,
    activeClockReminder: null,
};

function isClockReminderType(type: Outreach['outreach_type']): boolean {
    return type === 'clock_in' || type === 'clock_out';
}

function getMostRecentOutreach(
    outreaches: ReadonlyArray<Outreach>,
): Outreach | null {
    if (outreaches.length === 0) {
        return null;
    }

    return outreaches.reduce<Outreach | null>((latest, current) => {
        if (!latest) {
            return current;
        }

        const latestDate = latest.created_at;
        const currentDate = current.created_at;
        if (!latestDate || !currentDate) {
            return latest;
        }

        try {
            const latestTime = new Date(latestDate);
            const currentTime = new Date(currentDate);
            return currentTime > latestTime ? current : latest;
        } catch (error) {
            console.error('Date parsing error in outreach:', error);
            return latest;
        }
    }, null);
}

function getShiftCalloutStatus({
    shiftId,
    outreaches,
}: {
    readonly shiftId: string;
    readonly outreaches?: ReadonlyArray<Outreach>;
}): ShiftCalloutStatus {
    if (!outreaches) {
        return EMPTY_SHIFT_CALLOUT_STATUS;
    }

    const shiftOutreaches = outreaches.filter((outreach) =>
        outreach.shift_ids?.includes(shiftId),
    );

    const callouts = shiftOutreaches.filter(
        (outreach) =>
            !isClockReminderType(outreach.outreach_type) &&
            isActiveOutreach(outreach.status),
    );
    const clockReminders = shiftOutreaches.filter((outreach) =>
        isClockReminderType(outreach.outreach_type),
    );

    const activeCallout = getMostRecentOutreach(callouts);
    const activeClockReminder = getMostRecentOutreach(clockReminders);

    return {
        hasActiveCallout: activeCallout !== null,
        activeCallout,
        activeClockReminder,
    };
}

type ConfirmationStatus = 'confirmed' | 'declined';

type OrgClientRead = components['schemas']['OrgClientRead'];
const CONFIRMATION_STATUS_OPTIONS: ReadonlyArray<
    StatusOption<ConfirmationStatus>
> = [
    {
        value: 'confirmed',
        label: 'Confirmed',
        dotColor: 'bg-green-600',
    },
    {
        value: 'declined',
        label: 'Declined',
        dotColor: 'bg-red-600',
    },
];

const getStatusStyle = (status: ShiftStatus) => {
    switch (status) {
        case 'assigned':
        case 'assigned_and_confirmed':
            return {
                className: 'text-green-800 border-0',
                style: { backgroundColor: '#EDF3EC' },
            };
        case 'open':
        case 'needs_coverage':
            return {
                className: 'text-orange-800 border-0',
                style: { backgroundColor: '#FBF3DB' },
            };
        case 'cancelled':
            return {
                className: 'text-red-800 border-0',
                style: { backgroundColor: '#FDEBEC' },
            };
        case 'completed':
            return {
                className: 'text-blue-800 border-0',
                style: { backgroundColor: '#E1F0FF' },
            };
        case 'in_progress':
            return {
                className: 'text-purple-800 border-0',
                style: { backgroundColor: '#F3E8FF' },
            };
        case 'pending_confirmation':
            return {
                className: 'text-yellow-800 border-0',
                style: { backgroundColor: '#FEF3C7' },
            };
        case 'needs_attention':
            return {
                className: 'text-red-800 border-0',
                style: { backgroundColor: '#FEE2E2' },
            };
        case 'missing':
            return {
                className: 'text-amber-800 border-0',
                style: { backgroundColor: '#FEF3C7' },
            };
        case 'unknown':
        default:
            return {
                className: 'text-gray-800 border-0',
                style: { backgroundColor: '#F1F1EF' },
            };
    }
};

// ShiftStatus values that are also in UnifiedShiftStatus (for getStatusStyle)
type UnifiedShiftStatusSubset = Extract<UnifiedShiftStatus, ShiftStatus>;

const SHIFT_STATUS_VALUES: Array<UnifiedShiftStatusSubset> = [
    'open',
    'needs_coverage',
    'assigned',
    'pending_confirmation',
    'completed',
    'cancelled',
    'missing',
    'in_progress',
    'needs_attention',
    'not_scheduled',
    'unknown',
];

const isShiftStatus = (
    status: UnifiedShiftStatus,
): status is UnifiedShiftStatusSubset => {
    return (SHIFT_STATUS_VALUES as Array<string>).includes(status);
};

const getShiftTagStyle = (status: UnifiedShiftStatus | undefined | null) => {
    if (!status) {
        return { className: 'text-gray-800 border-0 bg-gray-100', style: {} };
    }

    // If it's a ShiftStatus, use getStatusStyle directly
    if (isShiftStatus(status)) {
        return getStatusStyle(status);
    }

    // Handle ShiftStage values that aren't ShiftStatus
    switch (status) {
        case 'confirmed':
            return getStatusStyle('assigned_and_confirmed');
        case 'declined':
            return getStatusStyle('cancelled');
        case 'needs_review':
            return getStatusStyle('missing');
        case 'no_response':
            return {
                className: 'text-amber-800 border-0 bg-amber-100',
                style: {},
            };
        case 'clock_in_reminder':
            return getStatusStyle('in_progress');
        case 'clock_out_reminder':
            return getStatusStyle('completed');
        case 'confirmation_follow_up':
            return getStatusStyle('pending_confirmation');
        default:
            return getStatusStyle('missing');
    }
};

const getShiftStageIndicatorColor = (status: UnifiedShiftStatus): string => {
    switch (status) {
        case 'confirmed':
        case 'completed':
        case 'in_progress':
            return '#16a34a'; // green-600
        case 'pending_confirmation':
        case 'confirmation_follow_up':
            return '#ca8a04'; // yellow-600
        case 'declined':
            return '#dc2626'; // red-600
        case 'needs_review':
        case 'missing':
        case 'no_response':
            return '#d97706'; // amber-600
        case 'cancelled':
            return '#64748b'; // slate-500
        default:
            return '#9ca3af'; // gray-400
    }
};

const formatShiftStatusText = (
    status: UnifiedShiftStatus | undefined | null,
): string => {
    if (!status) {
        return '-';
    }
    if (status === 'cancelled') {
        return 'Shift Cancelled';
    }
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

function isStatusToShowSentAt(status: UnifiedShiftStatus, shift: Shift) {
    return (
        !isEhrPendingConfirmation(shift.status, shift.latest_stage) &&
        [
            'pending_confirmation',
            'confirmed',
            'declined',
            'clock_in_reminder',
            'clocked_in',
            'clocked_out',
            'clock_out_reminder',
            'confirmation_follow_up',
        ].includes(status)
    );
}

function shouldIncludeInShiftCount(shift: Shift): boolean {
    if (shift.status === 'cancelled' || shift.latest_stage === 'cancelled') {
        return false;
    }

    return (
        Boolean(shift.assigned_caregiver_id) ||
        shift.status === 'needs_coverage'
    );
}

function getShiftDurationH(shift: Shift): number {
    return (
        (new Date(shift.shift_end_time).getTime() -
            new Date(shift.shift_start_time).getTime()) /
        3_600_000
    );
}

type ConfirmationStatusDropdownProps = {
    readonly status: UnifiedShiftStatus;
    readonly badge: React.ReactNode;
    readonly originalShift: Shift;
    readonly allowClockInWrite: boolean;
    readonly allowClockOutWrite: boolean;
    readonly onUpdateConfirmationStatus: (
        shift: Shift,
        status: ShiftStage,
    ) => Promise<void>;
    readonly onResetConfirmationStatus?: () => Promise<void>;
};

// Not used until we understand how this fits into the UI
export function ShiftStageDropdown({
    status,
    badge,
}: ConfirmationStatusDropdownProps) {
    // Needs the current existing statuses on page
    const statusOptions: Array<UnifiedShiftStatus> = [];

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>{badge}</DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                sideOffset={4}
                onClick={(event) => event.stopPropagation()}
            >
                {statusOptions.map((option) => {
                    const isActive = option === status;
                    const indicatorColor = getShiftStageIndicatorColor(option);

                    return (
                        <DropdownMenuItem
                            key={option}
                            className="flex items-center"
                            onSelect={(event) => {
                                event.stopPropagation();
                                if (isActive) {
                                    return;
                                }
                            }}
                        >
                            <div className="flex w-full items-center gap-2">
                                <span
                                    className="h-2.5 w-2.5 rounded-full"
                                    style={{
                                        backgroundColor: indicatorColor,
                                    }}
                                />
                                <span className="flex-1 text-sm">
                                    {formatShiftStatusText(option)}
                                </span>
                                {isActive ? (
                                    <Check className="text-muted-foreground h-3 w-3" />
                                ) : null}
                            </div>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function SkippedConfirmationTooltip() {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <span className="border-border text-muted-foreground rounded border px-1 text-[9px] uppercase">
                        Skipped Confirmation
                    </span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Configured to skip confirmations - you can change in
                        settings.
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

const createColumns = ({
    timezone,
    handleRowClick,
    onDeleteConfirmation,
    onSendConfirmation,
    onUpdateConfirmationStatus,
    onCreateCallout,
    calloutStatusMap,
    displayCaregiverNamesLastFirst,
    sortByLastName,
    primaryDataSourceType,
    allowClockInWrite,
    allowClockOutWrite,
    onUpdateShiftStage,
    getBlockedCaregiverInfos,
    onCaregiverUnblocked,
    careCoordinatorSupportEnabled,
    clientReferenceTerm,
    clientReferenceTermTitle,
    showDateColumn,
}: {
    handleRowClick: (shift: Shift) => void;
    timezone: string;
    onDeleteConfirmation: (confirmationId: string) => Promise<void>;
    onSendConfirmation: (shift: Shift) => Promise<void>;
    onUpdateConfirmationStatus: (
        shift: Shift,
        status: ConfirmationStatus,
    ) => Promise<void>;
    onCreateCallout: (shift: Shift) => void;
    calloutStatusMap: Map<string, ShiftCalloutStatus>;
    displayCaregiverNamesLastFirst: boolean;
    sortByLastName: boolean;
    primaryDataSourceType: DataSourceType | null;
    allowClockInWrite: boolean;
    allowClockOutWrite: boolean;
    onUpdateShiftStage: (shift: Shift, stage: ShiftStage) => Promise<void>;
    getBlockedCaregiverInfos?: (
        caregiverId: string,
    ) => Array<BlockedCaregiverInfo>;
    onCaregiverUnblocked?: () => void;
    careCoordinatorSupportEnabled: boolean;
    clientReferenceTerm: string;
    clientReferenceTermTitle: string;
    showDateColumn: boolean;
}): Array<ColumnDef<Shift>> => [
    {
        accessorKey: 'unread_message_count',
        size: 30,
        header: () => null,
        cell: ({ row }) => {
            const unreadMessageCount = Number(
                row.getValue('unread_message_count') ?? 0,
            );

            if (Number.isNaN(unreadMessageCount) || unreadMessageCount <= 0) {
                return null;
            }

            return (
                <span className="flex items-center justify-center">
                    <span
                        aria-hidden="true"
                        className="h-2 w-2 rounded-full bg-blue-500"
                    />
                </span>
            );
        },
    },
    ...(showDateColumn
        ? [
              {
                  id: 'shift_date',
                  accessorFn: (shift: Shift) => shift.shift_start_time,
                  header: ({ column }) => (
                      <DataTableColumnHeader column={column} title="Date" />
                  ),
                  cell: ({ row }) =>
                      formatFullDate(row.original.shift_start_time, timezone),
                  enableSorting: true,
              } satisfies ColumnDef<Shift>,
          ]
        : []),
    {
        accessorKey: 'shift_start_time',
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Start Time" />
        ),
        cell: ({ row }) => {
            const startTime = row.getValue('shift_start_time') as string;
            return formatTime(startTime, timezone);
        },
        enableSorting: true,
    },
    {
        accessorKey: 'shift_end_time',
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="End Time" />
        ),
        cell: ({ row }) => {
            const endTime = row.getValue('shift_end_time') as string;
            return formatTime(endTime, timezone);
        },
        enableSorting: true,
    },
    {
        // Make caregiver searchable/filterable via underlying accessor
        id: 'caregiver',
        accessorFn: (row) => row.caregiver?.name ?? '',
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Caregiver" />
        ),
        cell: ({ row }) => {
            const name = row.getValue('caregiver') as string;
            const caregiverId = row.original.caregiver?.id;
            const blockedInfos = caregiverId
                ? (getBlockedCaregiverInfos?.(caregiverId) ?? [])
                : [];

            if (!name) {
                return (
                    <span className="text-muted-foreground">Unassigned</span>
                );
            }

            return (
                <div className="flex items-center gap-2">
                    <span>
                        {formatCaregiverName(
                            name,
                            displayCaregiverNamesLastFirst,
                        )}
                    </span>
                    {blockedInfos.length > 0 && onCaregiverUnblocked && (
                        <BlockedPhonePopover
                            blockedInfos={blockedInfos}
                            onUnblocked={onCaregiverUnblocked}
                        />
                    )}
                </div>
            );
        },
        enableSorting: true,
        sortFn: (rowA, rowB, columnId) => {
            const nameA = rowA.getValue<string>(columnId) ?? '';
            const nameB = rowB.getValue<string>(columnId) ?? '';
            return compareCaregiverDisplayName(
                nameA,
                nameB,
                (name) =>
                    formatCaregiverName(name, displayCaregiverNamesLastFirst),
                sortByLastName,
            );
        },
        enableColumnFilter: true, // included in global filter
    },
    {
        accessorKey: 'patient_name',
        header: ({ column }) => (
            <DataTableColumnHeader
                column={column}
                title={clientReferenceTermTitle}
            />
        ),
        enableColumnFilter: true, // included in global filter
    },
    {
        accessorKey: 'patient_office_name',
        header: ({ column }) => (
            <DataTableColumnHeader
                column={column}
                title="Office"
                tooltip={`The office location associated with this ${clientReferenceTerm}`}
            />
        ),
        cell: ({ row }) => {
            const officeName = row.getValue('patient_office_name') as
                | string
                | null;
            return formatOfficeName(officeName);
        },
        enableColumnFilter: true, // enable faceted filter
        filterFn: 'inMultiSelect',
        meta: {
            displayName: 'Office',
        },
    },
    {
        accessorKey: 'patient_tags',
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Tags" />
        ),
        cell: ({ row }) => {
            const tags = row.original.patient_tags ?? [];
            if (tags.length === 0) {
                return <span className="text-muted-foreground">—</span>;
            }
            return (
                <div className="flex max-w-64 flex-wrap gap-1">
                    {tags.map((tag) => (
                        <Badge key={tag} variant="secondary">
                            {tag}
                        </Badge>
                    ))}
                </div>
            );
        },
        enableColumnFilter: true,
        filterFn: 'inMultiSelect',
        meta: {
            displayName: 'Tags',
        },
    },
    ...(careCoordinatorSupportEnabled
        ? [
              {
                  accessorKey: 'care_coordinator_names',
                  header: ({ column }) => (
                      <DataTableColumnHeader
                          column={column}
                          title="Care Coordinator"
                      />
                  ),
                  sortFn: (rowA, rowB) => {
                      const careCoordinatorNamesA =
                          rowA.original.care_coordinators
                              ?.map((coordinator) =>
                                  formatCoordinatorName(coordinator),
                              )
                              .sort() ?? ['No Care Coordinator'];
                      const careCoordinatorNamesB =
                          rowB.original.care_coordinators
                              ?.map((coordinator) =>
                                  formatCoordinatorName(coordinator),
                              )
                              .sort() ?? ['No Care Coordinator'];
                      return (careCoordinatorNamesA?.[0] ?? '').localeCompare(
                          careCoordinatorNamesB?.[0] ?? '',
                      );
                  },
                  cell: ({ row }) => {
                      const careCoordinators = row.original.care_coordinators;
                      if (!careCoordinators?.length) {
                          return (
                              <span>
                                  <i className="text-muted-foreground italic">
                                      No Care Coordinator
                                  </i>
                              </span>
                          );
                      }
                      const names = careCoordinators
                          .map((coordinator) =>
                              formatCoordinatorName(coordinator),
                          )
                          .sort()
                          .join(', ');
                      return <span>{names}</span>;
                  },
              } satisfies ColumnDef<Shift>,
          ]
        : []),
    {
        id: 'unified_status',
        accessorFn: (row) => resolveShiftStatus(row.status, row.latest_stage),
        header: ({ column }) => (
            <DataTableColumnHeader
                column={column}
                title="Status"
                tooltip="The statuses tracked within Phoebe"
            />
        ),
        enableColumnFilter: true,
        // Treat Open and Needs Coverage as one coverage bucket so filtering by
        // "Needs Coverage" also surfaces raw `open` shifts.
        filterFn: (row, columnId, filterValue) =>
            inMultiSelect(
                row,
                columnId,
                expandCoverageFilterValue(filterValue),
            ),
        meta: {
            displayName: 'Status',
        },
        cell: ({ row }) => {
            const shift = row.original as Shift;
            const status = row.getValue('unified_status') as UnifiedShiftStatus;

            const shiftId = shift.id;
            const { hasActiveCallout, activeCallout, activeClockReminder } =
                calloutStatusMap.get(shiftId) || {
                    hasActiveCallout: false,
                    activeCallout: null,
                    activeClockReminder: null,
                };

            const calloutBadge = hasActiveCallout && activeCallout && (
                <Link
                    key={activeCallout.id}
                    to="/outreaches/$callout-id"
                    params={{ 'callout-id': activeCallout.id }}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-block"
                >
                    <Badge
                        className={cn(
                            'rounded px-2 py-0.5 text-xs font-medium',
                            activeCallout.status === 'pending'
                                ? getStatusStyle('pending_confirmation')
                                      .className
                                : getStatusStyle('in_progress').className,
                        )}
                        style={
                            activeCallout.status === 'pending'
                                ? getStatusStyle('pending_confirmation').style
                                : getStatusStyle('in_progress').style
                        }
                    >
                        {activeCallout.status === 'pending'
                            ? 'Pending Outreach'
                            : 'Outreach In Progress'}
                    </Badge>
                </Link>
            );

            const hasError = !!shift.latest_stage_error;
            const statusStyle = getShiftTagStyle(status);
            const sentAt = shift.latest_stage_created_at;
            const showSentAt = sentAt && isStatusToShowSentAt(status, shift);

            const badgeElement = (
                <Badge
                    className={cn(
                        'rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap',
                        statusStyle.className,
                        'cursor-pointer focus:outline-none',
                    )}
                    style={statusStyle.style}
                    role="button"
                    tabIndex={0}
                >
                    <span className="flex items-center gap-1">
                        {hasError && <AlertCircle className="h-3 w-3" />}
                        {formatShiftStatusText(status)}
                    </span>
                </Badge>
            );

            let badge = hasError ? (
                <TooltipProvider delayDuration={150}>
                    <TooltipPrimitive.Root>
                        <TooltipTrigger asChild={true}>
                            {badgeElement}
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{shift.latest_stage_error}</p>
                        </TooltipContent>
                    </TooltipPrimitive.Root>
                </TooltipProvider>
            ) : showSentAt ? (
                <TooltipProvider delayDuration={150}>
                    <TooltipPrimitive.Root>
                        <TooltipTrigger asChild={true}>
                            {badgeElement}
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Sent: {formatTime(sentAt, timezone)}</p>
                        </TooltipContent>
                    </TooltipPrimitive.Root>
                </TooltipProvider>
            ) : (
                badgeElement
            );
            if (
                activeClockReminder &&
                (shift.latest_stage === 'clock_in_reminder' ||
                    shift.latest_stage === 'clock_out_reminder')
            ) {
                badge = (
                    <Link
                        key={activeClockReminder.id}
                        to="/outreaches/$callout-id"
                        params={{ 'callout-id': activeClockReminder.id }}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block"
                    >
                        {badge}
                    </Link>
                );
            }

            const processingState = shift.processing_state;
            const statusesWithPicker = new Set([
                'pending_confirmation',
                'declined',
                'needs_review',
                'confirmed',
                'confirmation_follow_up',
                'no_response',
                'assigned',
            ]);
            const isConfirmationStatus = (
                value: UnifiedShiftStatus,
            ): value is ConfirmationStatus =>
                value === 'declined' || value === 'confirmed';
            const canUpdateConfirmationStatus =
                shift.assigned_caregiver_id &&
                !shift.is_confirmation_skipped &&
                statusesWithPicker.has(status);
            const currentConfirmationStatus = isConfirmationStatus(status)
                ? status
                : null;

            const statusBadge = canUpdateConfirmationStatus ? (
                <StatusPicker
                    options={CONFIRMATION_STATUS_OPTIONS}
                    value={currentConfirmationStatus}
                    onSelect={(newStatus) => {
                        if (newStatus !== status) {
                            onUpdateConfirmationStatus(shift, newStatus);
                        }
                    }}
                >
                    <button type="button" className="appearance-none">
                        {badge}
                    </button>
                </StatusPicker>
            ) : (
                badge
            );

            return (
                <div className="flex flex-wrap items-center gap-2">
                    {statusBadge}
                    {calloutBadge}
                    {processingState === 'debouncing' && (
                        <Clock className="h-3 w-3" />
                    )}
                    {processingState === 'processing' && (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    )}
                    {shift.is_confirmation_skipped && (
                        <SkippedConfirmationTooltip />
                    )}
                </div>
            );
        },

        enableSorting: true,
        sortFn: (rowA, rowB) => {
            // Order: Open first, Confirmed/Assigned second, Pending third, Cancelled last
            const statusOrder: Record<UnifiedShiftStatus, number> = {
                // Group 0: Open (appear first - need assignment)
                open: 0,
                needs_coverage: 1,
                // Group 1: Confirmed/Assigned (appear second)
                confirmed: 10,
                clock_in_reminder: 12,
                in_progress: 13,
                clock_out_reminder: 14,
                completed: 15,
                confirmation_follow_up: 16,
                assigned: 20,
                // Group 2: Pending (appear third)
                pending_confirmation: 31,
                needs_review: 32,
                no_response: 33,
                needs_attention: 36,
                missing: 37,
                not_scheduled: 38,
                // Group 3: Cancelled (appear last)
                declined: 50,
                cancelled: 51,
                unknown: 999,
            };

            const statusA = rowA.getValue(
                'unified_status',
            ) as UnifiedShiftStatus;
            const statusB = rowB.getValue(
                'unified_status',
            ) as UnifiedShiftStatus;

            const orderA = statusOrder[statusA] ?? 999;
            const orderB = statusOrder[statusB] ?? 999;

            return orderA - orderB;
        },
    },
    {
        accessorKey: 'external_status',
        header: ({ column }) => {
            const ehrLabel = primaryDataSourceType
                ? `${getDataSourceDisplayName(primaryDataSourceType)} Status`
                : 'EHR Status';
            return (
                <DataTableColumnHeader
                    column={column}
                    title={ehrLabel}
                    tooltip="The shift status from the EHR, if it's synced from one"
                />
            );
        },
        cell: ({ row }) => {
            const shift = row.original;
            const externalStatus = shift.external_status as string | null;
            const syncedAt = shift.external_snapshot_synced_at as string | null;

            if (!externalStatus && !syncedAt) {
                return <span className="text-muted-foreground text-xs">—</span>;
            }
            return (
                <div className="flex flex-col gap-1">
                    {externalStatus && (
                        <span className="border-border text-muted-foreground w-fit rounded border px-1 text-[9px] uppercase">
                            {formatExternalStatus(externalStatus)}
                        </span>
                    )}
                    {syncedAt && (
                        <span className="text-muted-foreground text-xs">
                            Last synced at {formatTime(syncedAt, timezone)}
                        </span>
                    )}
                </div>
            );
        },
    },
    {
        id: 'actions',
        size: 40,
        cell: ({ row }) => {
            const shift = row.original;
            const { hasActiveCallout, activeCallout, activeClockReminder } =
                calloutStatusMap.get(shift.id) || {
                    hasActiveCallout: false,
                    activeCallout: null,
                    activeClockReminder: null,
                };

            const handleDeleteConfirmation = async () => {
                if (!shift.latest_confirmation_id) {
                    toast.error('No confirmation to delete', {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    });
                    return;
                }

                try {
                    await onDeleteConfirmation(shift.latest_confirmation_id);
                    toast.success('Shift confirmation deleted successfully', {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    });
                } catch (error) {
                    toast.error('Failed to delete shift confirmation', {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    });
                }
            };

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild={true}>
                        <Button
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                onSendConfirmation(shift);
                            }}
                            disabled={
                                shift.is_confirmation_skipped ||
                                new Date(shift.shift_start_time) < new Date()
                            }
                        >
                            Send Confirmation
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {hasActiveCallout && activeCallout ? (
                            <DropdownMenuItem asChild={true}>
                                <Link
                                    to="/outreaches/$callout-id"
                                    params={{ 'callout-id': activeCallout.id }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View Outreach
                                </Link>
                            </DropdownMenuItem>
                        ) : (
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCreateCallout(shift);
                                }}
                                disabled={
                                    new Date(shift.shift_end_time) < new Date()
                                }
                            >
                                Find Caregivers
                            </DropdownMenuItem>
                        )}
                        {activeClockReminder &&
                            (shift.latest_stage === 'clock_in_reminder' ||
                                shift.latest_stage ===
                                    'clock_out_reminder') && (
                                <DropdownMenuItem asChild={true}>
                                    <Link
                                        to="/outreaches/$callout-id"
                                        params={{
                                            'callout-id':
                                                activeClockReminder.id,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        View{' '}
                                        {activeClockReminder.outreach_type ===
                                        'clock_in'
                                            ? 'Clock In'
                                            : 'Clock Out'}{' '}
                                        Reminder
                                    </Link>
                                </DropdownMenuItem>
                            )}
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(shift);
                            }}
                        >
                            View Details
                        </DropdownMenuItem>
                        {shift.latest_confirmation_id && (
                            <React.Fragment>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteConfirmation();
                                    }}
                                    className="text-red-600"
                                    // disabled={hasActiveCallout}
                                >
                                    Delete Confirmation
                                </DropdownMenuItem>
                            </React.Fragment>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            );
        },
    },
];

type ShiftListPaneProps = {
    readonly initialDate?: string;
    readonly initialFrom?: string;
    readonly initialTo?: string;
    readonly initialStatus?: RouteShiftStatus;
    readonly initialClientIds?: Array<string>;
    readonly selectedShiftId?: string;
    readonly selectedShiftPathId?: string;
    readonly showApproveModal?: boolean;
    readonly calloutShiftId?: string;
};

type DisplayMode = 'grouped' | 'ungrouped';

type CaregiversData =
    ReturnType<typeof useOrganizationCaregivers> extends { data: infer Data }
        ? Data
        : undefined;

type Mode = ReturnType<typeof useMode>;

const COLUMN_VISIBILITY_KEY = 'phoebe-shift-list-pane-column-visibility';
const DEFAULT_PAGE_SIZE = 400;

export function shiftCareCoordinatorFilterStorageKey(
    organizationId: string,
    userId: string,
): string {
    return `phoebe:shifts:${organizationId}:${userId}:care-coordinator-filter`;
}

type SendPayload =
    | { readonly mode: 'ids'; readonly ids: Array<string> }
    | {
          readonly mode: 'date_range';
          readonly startIso: string;
          readonly endIso: string;
      };

type ShiftConfirmationBatchFailure = {
    readonly error: string;
};

const CONFIRMATION_ALREADY_EXISTS_ERROR = 'Confirmation already exists';
const CAREGIVER_MISSING_PHONE_ERROR = 'No SMS contact found for caregiver';

export function getMissingPhoneConfirmationError(
    totalCreated: number,
    failures: ReadonlyArray<ShiftConfirmationBatchFailure>,
): string | null {
    const missingPhoneCount = failures.filter(
        (failure) => failure.error === CAREGIVER_MISSING_PHONE_ERROR,
    ).length;
    if (missingPhoneCount === 0) {
        return null;
    }

    const sentMessage =
        totalCreated > 0
            ? `Sent ${totalCreated} confirmation${totalCreated === 1 ? '' : 's'}. `
            : '';
    const missingPhoneMessage = `Failed to send ${
        missingPhoneCount
    } confirmation${missingPhoneCount === 1 ? '' : 's'} because ${
        missingPhoneCount === 1
            ? 'the assigned caregiver doesn’t have a phone number'
            : 'the assigned caregivers don’t have phone numbers'
    }.`;
    const otherFailureCount = failures.filter(
        (failure) =>
            failure.error !== CAREGIVER_MISSING_PHONE_ERROR &&
            failure.error !== CONFIRMATION_ALREADY_EXISTS_ERROR,
    ).length;
    const otherFailureMessage =
        otherFailureCount > 0
            ? ` ${otherFailureCount} other confirmation${
                  otherFailureCount === 1 ? '' : 's'
              } failed to send.`
            : '';
    return sentMessage + missingPhoneMessage + otherFailureMessage;
}

function addDaysToDateKey(dateKey: string, days: number): string {
    const date = new Date(`${dateKey}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

export type UseShiftsDataParams = {
    readonly organizationId: string | null;
    readonly shiftStartIso: string | undefined;
    readonly shiftEndIso: string | undefined;
    readonly searchQuery: string;
    readonly columnFilters: ColumnFiltersState;
    readonly clientFilterIds: Array<string>;
    readonly careCoordinatorFilterIds: Array<string> | undefined;
    readonly includeUnassignedCareCoordinators: boolean;
    readonly pageSize: number;
    readonly careCoordinatorSupportEnabled: boolean;
};

export type ShiftStatsData = {
    readonly not_started: number;
    readonly pending: number;
    readonly confirmed: number;
    readonly cancelled: number;
    readonly other: number;
};

export const DEFAULT_STATS: ShiftStatsData = {
    not_started: 0,
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    other: 0,
};

export type UseShiftsDataResult = {
    readonly shifts: Array<Shift>;
    readonly totalShifts: number;
    readonly eligibleShiftCount: number;
    readonly eligibleShiftHours: number;
    readonly stats: ShiftStatsData;
    readonly isLoadingSearch: boolean;
    readonly isLoadingBase: boolean;
    readonly error: Error | undefined;
    readonly currentPage: number;
    readonly handlePageChange: (page: number) => void;
    readonly optimisticallyUpdateShifts: (
        updateFn: (shifts: Array<Shift>) => Array<Shift>,
        statsUpdateFn?: (stats: ShiftStatsData) => ShiftStatsData,
    ) => Promise<void>;
    readonly reloadShifts: () => Promise<void>;
    /** Whether server-side search is being used (when true, client-side filtering is skipped) */
    readonly shouldUseServerSearch: boolean;
    /** Available offices for filtering */
    readonly offices: ReadonlyArray<string> | undefined;
    /** Available client tags for filtering */
    readonly tags: ReadonlyArray<string> | undefined;
    /** Available care coordinators for filtering */
    readonly careCoordinators: ReadonlyArray<CareCoordinatorRead> | undefined;
    /** Compact client options for filtering */
    readonly clients: ReadonlyArray<ShiftFilterClient> | undefined;
};

export function useShiftsData({
    organizationId,
    shiftStartIso,
    shiftEndIso,
    searchQuery,
    columnFilters,
    clientFilterIds,
    careCoordinatorFilterIds,
    includeUnassignedCareCoordinators,
    pageSize,
    careCoordinatorSupportEnabled,
}: UseShiftsDataParams): UseShiftsDataResult {
    const [basePage, setBasePage] = useState(0);
    const [searchPage, setSearchPage] = useState(0);
    const [searchCursors, setSearchCursors] = useState<Record<number, string>>(
        {},
    );

    // Debounce prevents excessive API calls, defer keeps typing responsive
    const debouncedSearch = useDebouncedValue(searchQuery, 300);
    const deferredSearch = useDeferredValue(debouncedSearch);
    const columnFiltersKey = JSON.stringify(columnFilters);
    const clientFilterIdsKey = clientFilterIds.join(',');
    const careCoordinatorFilterIdsKey = careCoordinatorFilterIds?.join(',');

    // Reset base pagination when base query parameters change
    useEffect(() => {
        setBasePage(0);
    }, [
        shiftStartIso,
        shiftEndIso,
        clientFilterIdsKey,
        careCoordinatorFilterIdsKey,
    ]);

    // Reset search pagination and cursors when search or column filters change
    useEffect(() => {
        setSearchPage(0);
        setSearchCursors((current) =>
            Object.keys(current).length === 0 ? current : {},
        );
    }, [
        deferredSearch,
        columnFiltersKey,
        shiftStartIso,
        shiftEndIso,
        careCoordinatorFilterIdsKey,
        clientFilterIdsKey,
    ]);

    // Show toast when search query is too short
    useEffect(() => {
        if (deferredSearch && deferredSearch.length < 2) {
            toast.info('Search requires at least 2 characters', {
                persistNotification: false,
            });
        }
    }, [deferredSearch]);

    // Only search when query is at least 2 characters
    const isValidSearch = deferredSearch && deferredSearch.length >= 2;

    // Extract column filter values for server-side filtering
    const statusFilterValue = columnFilters.find(
        (f) => f.id === 'unified_status',
    )?.value as
        | {
              include?: Array<UnifiedShiftStatus>;
              exclude?: Array<UnifiedShiftStatus>;
          }
        | undefined;
    const officeFilterValue = columnFilters.find(
        (f) => f.id === 'patient_office_name',
    )?.value as
        | { include?: Array<string>; exclude?: Array<string> }
        | undefined;
    const tagFilterValue = columnFilters.find((f) => f.id === 'patient_tags')
        ?.value as
        | { include?: Array<string>; exclude?: Array<string> }
        | undefined;

    // Memoize filter arrays to prevent unnecessary re-renders
    const activeStatusFilters = useMemo(() => {
        // Expand so the coverage bucket (open + needs_coverage) stays together
        // for both server search and the "is not" complement.
        const included = expandCoverageStatuses(
            statusFilterValue?.include ?? [],
        );
        const excluded = expandCoverageStatuses(
            statusFilterValue?.exclude ?? [],
        );
        // For "is not" filters, compute complement: all statuses except excluded ones
        return excluded.length > 0
            ? ALL_UNIFIED_STATUS_VALUES.filter((s) => !excluded.includes(s))
            : included;
    }, [statusFilterValue?.include, statusFilterValue?.exclude]);

    // Check if any office filter is applied (for determining if server search is needed)
    const hasOfficeFilter =
        (officeFilterValue?.include?.length ?? 0) > 0 ||
        (officeFilterValue?.exclude?.length ?? 0) > 0;
    const hasTagFilter =
        (tagFilterValue?.include?.length ?? 0) > 0 ||
        (tagFilterValue?.exclude?.length ?? 0) > 0;

    const hasActiveFilters =
        activeStatusFilters.length > 0 || hasOfficeFilter || hasTagFilter;

    // Base query (no search) - uses realtime updates
    const {
        data: baseShiftsResponse,
        isLoading: isLoadingBase,
        error: baseError,
        mutate: mutateShifts,
    } = useShiftsRealtime(
        'shift-list-pane',
        {
            ...(shiftStartIso && { shift_start_date: shiftStartIso }),
            ...(shiftEndIso && { shift_end_date: shiftEndIso }),
            limit: pageSize,
            offset: basePage * pageSize,
            ...(careCoordinatorSupportEnabled &&
                careCoordinatorFilterIds && {
                    care_coordinator_ids: careCoordinatorFilterIds,
                }),
            ...(clientFilterIds.length > 0 && {
                client_ids: clientFilterIds,
            }),
            ...(careCoordinatorSupportEnabled &&
                includeUnassignedCareCoordinators && {
                    include_unassigned_care_coordinators: true,
                }),
        },
        { revalidateOnFocus: false },
    );

    // Use server-side search when there's an active search query or filters,
    // but only if the base response exceeds page size (otherwise client-side filtering is sufficient)
    const baseTotal = baseShiftsResponse?.total ?? 0;
    const shouldUseServerSearch =
        (isValidSearch || hasActiveFilters) && baseTotal > pageSize;

    // Client options stay compact even for large organizations; offices are
    // reused for server-side filtering when the current result is paginated.
    const { data: serverFilterValues } = useShiftSearchFilterValues(
        organizationId,
        { reason: 'shift-list-pane-filters' },
        { clientIds: clientFilterIds },
    );
    const { data: clientTagValues } = useOrganizationClientTags(organizationId);

    const allOffices = serverFilterValues?.offices;
    const allTags = clientTagValues?.tags;
    const clients = serverFilterValues?.clients;

    // Fetch care coordinators from dedicated endpoint
    const { data: careCoordinators } = useOrganizationCareCoordinators(
        careCoordinatorSupportEnabled ? organizationId : null,
        { reason: 'shift-list-pane-care-coordinators' },
    );

    // Compute active office filters for server-side search
    const activeOfficeFilters = useMemo(() => {
        const included = officeFilterValue?.include ?? [];
        const excluded = officeFilterValue?.exclude ?? [];
        // For "is not" filters, compute complement: all offices except excluded ones
        if (excluded.length > 0 && allOffices) {
            return allOffices.filter((o) => !excluded.includes(o));
        }
        return included;
    }, [officeFilterValue?.include, officeFilterValue?.exclude, allOffices]);

    // For cursor-based pagination: use cursor from previous page (if navigating beyond page 0)
    const searchCursor =
        searchPage > 0 ? searchCursors[searchPage - 1] : undefined;

    const {
        data: searchShiftsResponse,
        isLoading: isLoadingSearch,
        error: searchError,
        mutate: mutateSearchShifts,
    } = useShiftSearch(
        shouldUseServerSearch ? organizationId : null,
        shouldUseServerSearch && isValidSearch ? deferredSearch : null,
        { reason: 'shift-list-pane-search' },
        {
            ...(shiftStartIso && { shift_start_date: shiftStartIso }),
            ...(shiftEndIso && { shift_end_date: shiftEndIso }),
            limit: pageSize,
            ...(searchCursor && { cursor: searchCursor }),
            ...(careCoordinatorSupportEnabled &&
                careCoordinatorFilterIds && {
                    care_coordinator_ids: careCoordinatorFilterIds,
                }),
            ...(clientFilterIds.length > 0 && {
                client_ids: clientFilterIds,
            }),
            ...(careCoordinatorSupportEnabled &&
                includeUnassignedCareCoordinators && {
                    include_unassigned_care_coordinators: true,
                }),
            ...(activeStatusFilters.length > 0 && {
                effective_statuses: activeStatusFilters,
            }),
            ...(activeOfficeFilters.length > 0 && {
                patient_office_names: activeOfficeFilters,
            }),
            ...((tagFilterValue?.include?.length ?? 0) > 0 && {
                client_tags: tagFilterValue?.include,
            }),
            ...((tagFilterValue?.exclude?.length ?? 0) > 0 && {
                excluded_client_tags: tagFilterValue?.exclude,
            }),
        },
        { revalidateOnFocus: false, enabled: shouldUseServerSearch },
    );

    // Store next_cursor for search pagination when results arrive
    useEffect(() => {
        const nextCursor = searchShiftsResponse?.next_cursor;
        if (nextCursor) {
            setSearchCursors((prev) => ({
                ...prev,
                [searchPage]: nextCursor,
            }));
        }
    }, [searchShiftsResponse?.next_cursor, searchPage]);

    // Use search results when searching, otherwise use base results
    const shiftsResponse = shouldUseServerSearch
        ? searchShiftsResponse
        : baseShiftsResponse;
    const isLoadingSearchResult = shouldUseServerSearch
        ? isLoadingSearch
        : isLoadingBase;
    const error = shouldUseServerSearch ? searchError : baseError;

    // DataTable handles client-side filtering via columnFilters
    const shifts = useMemo(
        () => shiftsResponse?.shifts ?? [],
        [shiftsResponse?.shifts],
    );

    const eligibleShiftCount = shiftsResponse?.eligible_shift_count ?? 0;
    const eligibleShiftHours = shiftsResponse?.eligible_shift_hours ?? 0;

    // When all data fits in one page, compute stats from in-memory shifts
    // (respects client-side filtering). Otherwise use server stats.
    const stats = useMemo<ShiftStatsData>(() => {
        if ((baseShiftsResponse?.total ?? 0) > pageSize) {
            return baseShiftsResponse?.stats ?? DEFAULT_STATS;
        }
        return shifts.reduce(
            (acc, shift) => {
                if (shift.latest_stage === 'confirmed') {
                    acc.confirmed++;
                } else if (
                    shift.latest_stage === 'pending_confirmation' ||
                    shift.latest_stage === 'confirmation_follow_up'
                ) {
                    acc.pending++;
                } else if (
                    !shift.latest_confirmation_id &&
                    shift.assigned_caregiver_id
                ) {
                    acc.not_started++;
                } else {
                    acc.other++;
                }

                if (
                    shift.latest_stage === 'cancelled' ||
                    shift.latest_stage === 'declined'
                ) {
                    acc.cancelled++;
                }

                return acc;
            },
            {
                not_started: 0,
                pending: 0,
                confirmed: 0,
                cancelled: 0,
                other: 0,
            },
        );
    }, [
        baseShiftsResponse?.total,
        baseShiftsResponse?.stats,
        shifts,
        pageSize,
    ]);

    // Use separate page state for search vs base results
    const currentPage = shouldUseServerSearch ? searchPage : basePage;
    const handlePageChange = useCallback(
        (page: number) => {
            if (shouldUseServerSearch) {
                setSearchPage(page);
            } else {
                setBasePage(page);
            }
        },
        [shouldUseServerSearch],
    );

    // Helper to optimistically update both base and search shift caches
    const optimisticallyUpdateShifts = useCallback(
        async (
            updateFn: (shifts: Array<Shift>) => Array<Shift>,
            statsUpdateFn?: (stats: ShiftStatsData) => ShiftStatsData,
        ) => {
            await mutateShifts(
                (currentData) => {
                    if (!currentData?.shifts) {
                        return currentData;
                    }
                    return {
                        ...currentData,
                        shifts: updateFn(currentData.shifts),
                        stats:
                            statsUpdateFn && currentData.stats
                                ? statsUpdateFn(currentData.stats)
                                : currentData.stats,
                    };
                },
                { revalidate: false },
            );

            if (shouldUseServerSearch) {
                await mutateSearchShifts(
                    (currentData) => {
                        if (!currentData?.shifts) {
                            return currentData;
                        }
                        return {
                            ...currentData,
                            shifts: updateFn(currentData.shifts),
                        };
                    },
                    { revalidate: false },
                );
            }
        },
        [mutateShifts, mutateSearchShifts, shouldUseServerSearch],
    );

    // Helper to reload shifts from server (revalidate caches)
    const reloadShifts = useCallback(async () => {
        await mutateShifts();
        if (shouldUseServerSearch) {
            await mutateSearchShifts();
        }
    }, [mutateShifts, mutateSearchShifts, shouldUseServerSearch]);

    return {
        shifts,
        totalShifts: shiftsResponse?.total ?? 0,
        eligibleShiftCount,
        eligibleShiftHours,
        stats,
        isLoadingSearch: isLoadingSearchResult,
        isLoadingBase,
        error,
        currentPage,
        handlePageChange,
        optimisticallyUpdateShifts,
        reloadShifts,
        shouldUseServerSearch,
        offices: allOffices,
        tags: allTags,
        careCoordinators,
        clients,
    };
}

export function ShiftListPane({
    initialDate,
    initialFrom,
    initialTo,
    initialStatus,
    initialClientIds,
    selectedShiftId,
    selectedShiftPathId,
    showApproveModal,
    calloutShiftId,
}: ShiftListPaneProps) {
    const posthog = usePostHog();
    const { data: user } = useUser({ reason: 'shift-list-pane' });
    const organizationId = useOrganizationId();
    const pageSize = DEFAULT_PAGE_SIZE;
    const FILTER_KEY_PREFIX = `phoebe:shifts:${organizationId ?? 'anon'}`;
    const { data: latestSyncLog } = useLatestShiftsSync(organizationId, {
        reason: 'shift-list-pane',
    });
    const { timezone } = useOrganizationTimezone();
    const mode = useMode();
    const isMobile = useIsMobile();
    const columnVisibilityKey = isMobile
        ? `${COLUMN_VISIBILITY_KEY}-mobile`
        : COLUMN_VISIBILITY_KEY;
    const initialColumnVisibility: Record<string, boolean> = isMobile
        ? {
              shift_end_time: false,
              patient_office_name: false,
              patient_tags: false,
              care_coordinator_names: false,
              external_status: false,
          }
        : { patient_tags: false };
    const { data: organization } = useOrganization(organizationId, {
        reason: 'shift-list-pane',
    });
    const { data: caregivers } = useOrganizationCaregivers(organizationId, {
        reason: 'shift-list-pane',
    });
    const careCoordinatorSupportEnabled =
        !!organization?.features?.care_coordinator_support_enabled;
    const isOrganizationLoaded = organization !== undefined;

    const createShiftEnabled =
        !!organization?.features?.['create_shift_enabled'];

    const { data: myCareCoordinator } = useMyCareCoordinator(
        careCoordinatorSupportEnabled ? organizationId : null,
        { reason: 'shift-list-pane' },
    );
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        {
            reason: 'shift-list-pane',
        },
    );

    // Get caregiver IDs for blocked phone check
    const caregiverIdsForBlockedCheck = useMemo(
        () => caregivers?.map((c) => c.id) ?? null,
        [caregivers],
    );

    const { data: blockedCaregivers, mutate: mutateBlockedCaregivers } =
        useBlockedCaregivers(organizationId, caregiverIdsForBlockedCheck);

    const blockedCaregiverMap = useMemo(() => {
        const map = new Map<string, Array<BlockedCaregiverInfo>>();
        blockedCaregivers?.forEach((b) => {
            const existing = map.get(b.caregiver_id) ?? [];
            existing.push(b);
            map.set(b.caregiver_id, existing);
        });
        return map;
    }, [blockedCaregivers]);

    const getBlockedCaregiverInfos = useCallback(
        (caregiverId: string) => blockedCaregiverMap.get(caregiverId) ?? [],
        [blockedCaregiverMap],
    );

    const hasWellSkyAccount = useMemo(
        () =>
            dataSourceAccounts?.some((account) => account.type === 'wellsky') ??
            false,
        [dataSourceAccounts],
    );
    // Backend `POST /shifts/{id}/sync` supports WellSky, AxisCare and
    // HHAExchange when the matching DSA is sync_enabled. Don't show the
    // per-shift sync option otherwise — the click would 4xx.
    const hasSyncEnabledAccountForShift = useMemo(
        () =>
            dataSourceAccounts?.some(
                (account) =>
                    (account.type === 'wellsky' ||
                        account.type === 'axiscare' ||
                        account.type === 'hhaexchange') &&
                    account.sync_enabled,
            ) ?? false,
        [dataSourceAccounts],
    );

    // Form for caregiver selection in sandbox mode
    const caregiverForm = useForm<{ caregiver: string }>({
        defaultValues: { caregiver: '' },
    });

    const navigate = useNavigate();
    const router = useRouter();
    const search = useSearch({ strict: false }) as {
        date?: string;
        from?: string;
        to?: string;
        status?: RouteShiftStatus;
        clientIds?: Array<string>;
        shiftId?: string;
        approve?: boolean;
        calloutShiftId?: string;
        organizationId?: string;
        mode?: 'live' | 'sandbox';
    };
    const selectedShiftIdFromSearch = search?.shiftId;

    const [selectedDateRange, setSelectedDateRange] =
        useState<DateRangePickerValue>(() =>
            resolveInitialShiftRange({
                from: initialFrom,
                to: initialTo,
                date: initialDate,
                storedRange: null,
                todayKey: toDateKey(new Date(), timezone),
            }),
        );
    const [hasLoadedDateFromStorage, setHasLoadedDateFromStorage] =
        useState(false);

    // True only when the URL params are present AND well-formed. Invalid URL
    // params must not suppress the localStorage read or overwrite the stored range.
    const hasValidUrlParams = useMemo(
        () =>
            (!!initialFrom &&
                !!initialTo &&
                isValidDateKey(initialFrom) &&
                isValidDateKey(initialTo)) ||
            (!!initialDate && isValidDateKey(initialDate)),
        [initialFrom, initialTo, initialDate],
    );

    // Resync the range when the URL provides explicit date params.
    useEffect(() => {
        if (hasValidUrlParams) {
            setSelectedDateRange(
                resolveInitialShiftRange({
                    from: initialFrom,
                    to: initialTo,
                    date: initialDate,
                    storedRange: null,
                    todayKey: toDateKey(new Date(), timezone),
                }),
            );
        }
    }, [hasValidUrlParams, initialFrom, initialTo, initialDate, timezone]);

    // Reset storage load state when org changes
    useEffect(() => {
        setHasLoadedDateFromStorage(false);
    }, [organizationId]);

    // Load the stored range when returning to shifts without URL date params
    useEffect(() => {
        if (
            hasLoadedDateFromStorage ||
            !organizationId ||
            typeof window === 'undefined'
        ) {
            return;
        }

        if (hasValidUrlParams) {
            setHasLoadedDateFromStorage(true);
            return;
        }

        const stored = parseShiftRange(
            localStorage.getItem(shiftRangeStorageKey(organizationId)),
        );
        const todayKey = toDateKey(new Date(), timezone);
        setSelectedDateRange(stored ?? { from: todayKey, to: todayKey });
        setHasLoadedDateFromStorage(true);
    }, [hasLoadedDateFromStorage, hasValidUrlParams, organizationId, timezone]);

    // Persist the selected range after initial load (or when URL provides it)
    useEffect(() => {
        if (!organizationId || typeof window === 'undefined') {
            return;
        }
        if (!hasValidUrlParams && !hasLoadedDateFromStorage) {
            return;
        }

        localStorage.setItem(
            shiftRangeStorageKey(organizationId),
            serializeShiftRange(selectedDateRange),
        );
    }, [
        organizationId,
        selectedDateRange,
        hasValidUrlParams,
        hasLoadedDateFromStorage,
    ]);

    const [isSendingConfirmations, setIsSendingConfirmations] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isSyncingSelectedShift, setIsSyncingSelectedShift] = useState(false);
    const pendingSelectedShiftSyncIdRef = useRef<string | null>(null);
    const [showCreateOutreachModal, setShowCreateOutreachModal] =
        useState(false);
    const [selectedShiftIdsForOutreach, setSelectedShiftIdsForOutreach] =
        useState<Set<string>>(new Set());
    const handledCreateCalloutShiftIdRef = useRef<string | null>(null);
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
    const [showSandboxSetupModal, setShowSandboxSetupModal] = useState(false);
    const [showCreateTestShiftModal, setShowCreateTestShiftModal] =
        useState(false);
    const [sendPayload, setSendPayload] = useState<SendPayload | null>(null);
    const [displayMode, _] = useState<DisplayMode>('ungrouped');
    const [searchQuery, setSearchQuery] = useState('');
    const {
        columnFilters,
        clientFilterIds,
        updateColumnFilters,
        updateClientFilter,
        toggleClientFilter,
    } = useShiftListRouteFilters({
        organizationId,
        initialStatus,
        initialClientIds,
        selectedDateRange,
        selectedShiftPathId,
    });

    // Care coordinator filter state
    // null = not initialized, [] = show all, [...ids] = filter by these IDs
    const [careCoordinatorFilter, setCareCoordinatorFilter] =
        useState<Array<string> | null>(null);
    const careCoordinatorFilterStorageKey =
        organizationId && user?.id
            ? shiftCareCoordinatorFilterStorageKey(organizationId, user.id)
            : null;
    const [loadedCareCoordinatorFilterStorageKey, setLoadedStorageKey] =
        useState<string | null>(null);

    // Wait for both identities and feature flags so one user's selection can
    // never be loaded or persisted for another user in the same organization.
    useEffect(() => {
        if (
            !careCoordinatorFilterStorageKey ||
            !isOrganizationLoaded ||
            typeof window === 'undefined'
        ) {
            return;
        }

        setCareCoordinatorFilter(null);
        setLoadedStorageKey(null);

        // The legacy key cannot be safely migrated because it has no owner.
        const legacyStorageKey = `phoebe:shifts:${organizationId}:care-coordinator-filter`;
        localStorage.removeItem(legacyStorageKey);

        if (!careCoordinatorSupportEnabled) {
            localStorage.removeItem(careCoordinatorFilterStorageKey);
            setCareCoordinatorFilter([]);
            setLoadedStorageKey(careCoordinatorFilterStorageKey);
            return;
        }

        const stored = localStorage.getItem(careCoordinatorFilterStorageKey);
        if (stored !== null) {
            try {
                setCareCoordinatorFilter(JSON.parse(stored) as Array<string>);
            } catch {
                // Invalid stored value, will fall through to myCareCoordinator default
            }
        }
        setLoadedStorageKey(careCoordinatorFilterStorageKey);
    }, [
        careCoordinatorFilterStorageKey,
        careCoordinatorSupportEnabled,
        isOrganizationLoaded,
        organizationId,
    ]);

    // Persist filter to localStorage when it changes
    useEffect(() => {
        if (
            !careCoordinatorFilterStorageKey ||
            loadedCareCoordinatorFilterStorageKey !==
                careCoordinatorFilterStorageKey ||
            !careCoordinatorSupportEnabled ||
            careCoordinatorFilter === null
        ) {
            return;
        }
        localStorage.setItem(
            careCoordinatorFilterStorageKey,
            JSON.stringify(careCoordinatorFilter),
        );
    }, [
        careCoordinatorFilter,
        careCoordinatorFilterStorageKey,
        careCoordinatorSupportEnabled,
        loadedCareCoordinatorFilterStorageKey,
    ]);

    // Auto-initialize filter to user's care coordinator when data loads
    // (only after we've checked localStorage, to avoid race conditions)
    useEffect(() => {
        if (
            careCoordinatorSupportEnabled &&
            careCoordinatorFilterStorageKey !== null &&
            loadedCareCoordinatorFilterStorageKey ===
                careCoordinatorFilterStorageKey &&
            careCoordinatorFilter === null &&
            myCareCoordinator
        ) {
            setCareCoordinatorFilter([myCareCoordinator.id]);
        }
    }, [
        careCoordinatorFilter,
        careCoordinatorFilterStorageKey,
        careCoordinatorSupportEnabled,
        loadedCareCoordinatorFilterStorageKey,
        myCareCoordinator,
    ]);

    // Compute the filter IDs and unassigned flag to pass to the API
    const { careCoordinatorFilterIds, includeUnassignedCareCoordinators } =
        useMemo(() => {
            if (
                !careCoordinatorSupportEnabled ||
                !careCoordinatorFilterStorageKey ||
                loadedCareCoordinatorFilterStorageKey !==
                    careCoordinatorFilterStorageKey
            ) {
                return {
                    careCoordinatorFilterIds: undefined,
                    includeUnassignedCareCoordinators: false,
                };
            }

            if (careCoordinatorFilter === null) {
                // Not yet initialized - use the user's care coordinator if available
                return {
                    careCoordinatorFilterIds: myCareCoordinator
                        ? [myCareCoordinator.id]
                        : undefined,
                    includeUnassignedCareCoordinators: false,
                };
            }
            if (careCoordinatorFilter.length === 0) {
                // Explicitly cleared - show all (no filter)
                return {
                    careCoordinatorFilterIds: undefined,
                    includeUnassignedCareCoordinators: false,
                };
            }

            // Check if "unassigned" is selected and filter it out of actual IDs
            const includeUnassigned = careCoordinatorFilter.includes(
                UNASSIGNED_CARE_COORDINATOR_ID,
            );
            const actualIds = careCoordinatorFilter.filter(
                (id) => id !== UNASSIGNED_CARE_COORDINATOR_ID,
            );

            // When includeUnassigned is true but no actual IDs, pass empty array
            // (backend requires care_coordinator_ids to be non-null for unassigned filter to apply)
            return {
                careCoordinatorFilterIds:
                    actualIds.length > 0
                        ? actualIds
                        : includeUnassigned
                          ? []
                          : undefined,
                includeUnassignedCareCoordinators: includeUnassigned,
            };
        }, [
            careCoordinatorFilter,
            careCoordinatorFilterStorageKey,
            careCoordinatorSupportEnabled,
            loadedCareCoordinatorFilterStorageKey,
            myCareCoordinator,
        ]);

    const todayDateKey = toDateKey(new Date(), timezone);
    const isTodaySelected = isTodayRange(selectedDateRange, todayDateKey);
    const isSelectedDateInPast = isRangeEntirelyInPast(
        selectedDateRange,
        todayDateKey,
    );

    const shiftStartIso = dateKeyToStartUtcIso(
        selectedDateRange.from,
        timezone,
    );
    const shiftEndIso = dateKeyToEndUtcIso(selectedDateRange.to, timezone);

    // Centralized shifts data management
    const {
        shifts,
        totalShifts,
        eligibleShiftCount,
        eligibleShiftHours,
        stats,
        isLoadingSearch,
        isLoadingBase,
        error,
        currentPage,
        handlePageChange,
        optimisticallyUpdateShifts,
        reloadShifts,
        shouldUseServerSearch,
        offices,
        tags,
        careCoordinators,
        clients: filterClients,
    } = useShiftsData({
        organizationId,
        shiftStartIso,
        shiftEndIso,
        searchQuery,
        columnFilters,
        clientFilterIds,
        careCoordinatorFilterIds,
        includeUnassignedCareCoordinators,
        pageSize,
        careCoordinatorSupportEnabled,
    });

    const eligibleShiftCountForHeader = useMemo(() => {
        if (totalShifts > shifts.length) {
            return eligibleShiftCount;
        }

        return shifts.filter(shouldIncludeInShiftCount).length;
    }, [eligibleShiftCount, totalShifts, shifts]);

    const shiftCountForHeader = eligibleShiftCountForHeader;
    const shiftHoursForHeader = useMemo(() => {
        if (totalShifts > shifts.length) {
            return eligibleShiftHours;
        }

        return shifts
            .filter(shouldIncludeInShiftCount)
            .reduce((totalH, shift) => totalH + getShiftDurationH(shift), 0);
    }, [eligibleShiftHours, totalShifts, shifts]);

    // Value to pass to child components that need to sync state
    // (preserves "show all" as [] instead of undefined)
    const careCoordinatorFilterIdsForSync =
        careCoordinatorFilter === null ? undefined : careCoordinatorFilter;

    const handleSelectCoordinator = useCallback((coordinatorId: string) => {
        setCareCoordinatorFilter((current) => {
            const currentIds = current ?? [];
            if (currentIds.includes(coordinatorId)) {
                return currentIds.filter((id) => id !== coordinatorId);
            }
            return [...currentIds, coordinatorId];
        });
    }, []);

    const handleShowAllCoordinators = useCallback(() => {
        setCareCoordinatorFilter([]);
    }, []);

    const selectedShiftDetailId =
        selectedShiftId ??
        selectedShiftIdFromSearch ??
        selectedShift?.id ??
        null;
    const selectedShiftOrganizationId =
        selectedShift?.organization_id ?? organizationId ?? null;

    const {
        data: selectedShiftDetails,
        error: selectedShiftDetailsError,
        mutate: mutateSelectedShiftDetails,
    } = useShift(
        selectedShiftOrganizationId,
        selectedShiftDetailId,
        { reason: 'shift-list-pane-detail' },
        { include_caregiver_contacts: true },
    );
    const isMissingSelectedShiftInCurrentOrganization =
        selectedShiftDetailsError instanceof ApiError &&
        selectedShiftDetailsError.status === 404;
    useResourceOrganizationLookup({
        enabled:
            !!selectedShiftDetailId &&
            isMissingSelectedShiftInCurrentOrganization,
        resourceId: selectedShiftDetailId,
        resourceType: 'shift',
    });

    const displayCaregiverNamesLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;
    const sortByLastName =
        organization?.features?.last_name_sorting_enabled === true;
    const clientReferenceTerm =
        organization?.general_settings?.client_reference_term ?? 'client';
    const clientReferenceTermTitle =
        formatClientReferenceTerm(clientReferenceTerm);

    // All assigned & not skipped shift IDs (for fallback and comparison)
    const allAssignedShiftIds = useMemo(
        () =>
            shifts
                .filter(
                    (s) =>
                        s.assigned_caregiver_id && !s.is_confirmation_skipped,
                )
                .map((s) => s.id),
        [shifts],
    );

    // Find the selected shift if selectedShiftId is provided
    useEffect(() => {
        const id = selectedShiftId ?? selectedShiftIdFromSearch;
        if (id && shifts.length > 0) {
            const shift = shifts.find((s) => s.id === id);
            if (shift) {
                setSelectedShift(shift);
            }
        }
    }, [selectedShiftId, selectedShiftIdFromSearch, shifts]);

    // Get pending messages count (filtered by care coordinator if enabled)
    const {
        data: pendingMessagesCount,
        isLoading: isLoadingPendingMessagesCount,
        mutate: mutatePendingMessagesCount,
    } = useOrganizationPendingMessagesCountRealtime('shift-list-pane', {
        organizationId,
        domain: 'shift_confirmation',
        careCoordinatorIds: careCoordinatorSupportEnabled
            ? careCoordinatorFilterIds
            : undefined,
    });

    const lastFetched = latestSyncLog?.started_at
        ? formatRelativeTime(latestSyncLog.started_at)
        : null;

    const shiftIds = useMemo(() => shifts.map((s) => s.id), [shifts]);

    const clockInOutEnabled =
        !!organization?.features?.['clock_in_out_processing_enabled'];

    // Only fetch clock_in/clock_out outreach types when the feature flag is enabled
    const outreachTypesToFetch = useMemo((): Array<OutreachType> => {
        if (clockInOutEnabled) {
            return ['callout', 'recurring_shift', 'clock_in', 'clock_out'];
        }
        // When clock in/out is disabled, only fetch callout-related types
        return ['callout', 'recurring_shift'];
    }, [clockInOutEnabled]);

    const { data: outreaches } = useOutreachesBatch(
        organizationId,
        shiftIds,
        {
            reason: 'shift-list-pane',
        },
        {
            outreachTypes: outreachTypesToFetch,
        },
    );

    const calloutStatusMap = useMemo(() => {
        const map = new Map<string, ShiftCalloutStatus>();

        if (!outreaches) {
            return map;
        }

        shifts.forEach((shift) => {
            map.set(
                shift.id,
                getShiftCalloutStatus({
                    shiftId: shift.id,
                    outreaches,
                }),
            );
        });

        return map;
    }, [shifts, outreaches]);

    const selectedShiftCalloutStatus = selectedShift
        ? calloutStatusMap.get(selectedShift.id)
        : undefined;
    const hasActiveCalloutForSelectedShift =
        selectedShiftCalloutStatus?.hasActiveCallout ?? false;
    const activeCalloutForSelectedShift =
        selectedShiftCalloutStatus?.activeCallout ?? null;
    const activeClockReminderForSelectedShift =
        selectedShiftCalloutStatus?.activeClockReminder ?? null;

    useEffect(() => {
        if (!selectedShift?.id) {
            return;
        }

        optimisticallyUpdateShifts((shifts) =>
            shifts.map((shift) =>
                shift.id === selectedShift.id ||
                shift.assigned_caregiver_id ===
                    selectedShift.assigned_caregiver_id
                    ? {
                          ...shift,
                          unread_message_count: 0,
                      }
                    : shift,
            ),
        );
    }, [
        selectedShift?.id,
        selectedShift?.assigned_caregiver_id,
        optimisticallyUpdateShifts,
    ]);

    useListenForOrganizationEvent('shifts_updated', () => {
        // naively reset the syncing state
        setIsSyncingSelectedShift(false);
    });

    useEffect(() => {
        if (organizationId) {
            posthog.capture(EVENTS.SHIFT_CONFIRMATION_PAGE_LOADED, {
                organization_id: organizationId,
                user_id: user?.id,
                selected_date: selectedDateRange.from,
            });
        }
    }, [
        organizationId,
        posthog,
        user,
        selectedDateRange.from,
        selectedDateRange.to,
    ]);

    const currentSearch = useSearch({ strict: false });
    const currentShiftRouteSearch = useMemo(
        () => ({
            // Always propagate the in-memory range so navigations that spread
            // this (approve open/close, callout cleanup) preserve the range
            // even when it came from localStorage or a legacy ?date= link.
            from: selectedDateRange.from,
            to: selectedDateRange.to,
            status: isRouteShiftStatus(currentSearch.status)
                ? currentSearch.status
                : undefined,
            clientIds: Array.isArray(currentSearch.clientIds)
                ? currentSearch.clientIds.filter(
                      (id): id is string => typeof id === 'string',
                  )
                : undefined,
            shiftId:
                typeof currentSearch.shiftId === 'string'
                    ? currentSearch.shiftId
                    : undefined,
            approve: currentSearch.approve === true ? true : undefined,
            calloutShiftId:
                typeof currentSearch.calloutShiftId === 'string'
                    ? currentSearch.calloutShiftId
                    : undefined,
            organizationId:
                typeof currentSearch.organizationId === 'string'
                    ? currentSearch.organizationId
                    : undefined,
            mode:
                currentSearch.mode === 'live' ||
                currentSearch.mode === 'sandbox'
                    ? currentSearch.mode
                    : undefined,
        }),
        [currentSearch, selectedDateRange],
    );

    const buildShiftRouteOptions = useCallback(
        (shift: Shift) => {
            return {
                to: '/shifts',
                search: {
                    ...currentShiftRouteSearch,
                    from: selectedDateRange.from,
                    to: selectedDateRange.to,
                    shiftId: shift.id,
                },
            } as const;
        },
        [currentShiftRouteSearch, selectedDateRange],
    );

    const handleRowClick = useCallback(
        (shift: Shift) => {
            setSelectedShift(shift);
            navigate({
                ...buildShiftRouteOptions(shift),
                replace: false,
            });
        },
        [buildShiftRouteOptions, navigate],
    );

    const getShiftNavigation = useCallback(
        (shift: Shift) => {
            const routeOptions = buildShiftRouteOptions(shift);
            return {
                href: router.buildLocation(routeOptions).href,
                navigate: () => {
                    setSelectedShift(shift);
                    void router.navigate(routeOptions);
                },
            };
        },
        [buildShiftRouteOptions, router],
    );

    const handleDeleteShiftConfirmation = useCallback(
        async (confirmationId: string) => {
            if (!organizationId) {
                throw new Error('Organization ID is required');
            }

            // Capture previous state for potential rollback
            const previousShiftState = shifts?.find(
                (s) => s.latest_confirmation_id === confirmationId,
            );

            // Determine which stat counter to decrement based on previous stage
            const previousStage = previousShiftState?.latest_stage;
            const isPending =
                previousStage === 'pending_confirmation' ||
                previousStage === 'confirmation_follow_up';
            const isConfirmed = previousStage === 'confirmed';

            await optimisticallyUpdateShifts(
                (shifts) =>
                    shifts.map((s) =>
                        s.latest_confirmation_id === confirmationId
                            ? {
                                  ...s,
                                  latest_confirmation_id: null,
                                  latest_stage: null,
                                  latest_stage_created_at: null,
                              }
                            : s,
                    ),
                (stats) => {
                    if (!stats) {
                        return stats;
                    }
                    return {
                        ...stats,
                        not_started: stats.not_started + 1,
                        pending: isPending
                            ? Math.max(0, stats.pending - 1)
                            : stats.pending,
                        confirmed: isConfirmed
                            ? Math.max(0, stats.confirmed - 1)
                            : stats.confirmed,
                    };
                },
            );

            try {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/shift-confirmations/{confirmation_id}',
                    pathParams: {
                        organization_id: organizationId,
                        confirmation_id: confirmationId,
                    },
                });

                posthog.capture(EVENTS.SHIFT_CONFIRMATION_DELETE_CONFIRMATION, {
                    organization_id: organizationId,
                    user_id: user?.id,
                    confirmation_id: confirmationId,
                    selected_date: selectedDateRange.from,
                });

                // Refresh pending messages count after deleting a confirmation
                mutatePendingMessagesCount();
            } catch (error) {
                console.error('Error deleting shift confirmation:', error);
                toast.error('Failed to delete shift confirmation', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });

                // Restore previous state including stats
                if (previousShiftState) {
                    await optimisticallyUpdateShifts(
                        (shifts) =>
                            shifts.map((s) =>
                                s.id === previousShiftState.id
                                    ? {
                                          ...s,
                                          latest_confirmation_id:
                                              previousShiftState.latest_confirmation_id,
                                          latest_stage:
                                              previousShiftState.latest_stage,
                                          latest_stage_created_at:
                                              previousShiftState.latest_stage_created_at,
                                      }
                                    : s,
                            ),
                        (stats) => {
                            if (!stats) {
                                return stats;
                            }
                            return {
                                ...stats,
                                not_started: Math.max(0, stats.not_started - 1),
                                pending: isPending
                                    ? stats.pending + 1
                                    : stats.pending,
                                confirmed: isConfirmed
                                    ? stats.confirmed + 1
                                    : stats.confirmed,
                            };
                        },
                    );
                }
            }
        },
        [
            organizationId,
            posthog,
            user,
            selectedDateRange,
            optimisticallyUpdateShifts,
            mutatePendingMessagesCount,
            shifts,
        ],
    );

    const optimisticallyMarkShiftsPending = useCallback(
        async (shiftIds: Array<string>) => {
            // Count how many shifts will be moved from not_started to pending
            let shiftsMovedToPending = 0;

            const updateShifts = (shifts: Array<Shift>) =>
                shifts.map((shift) => {
                    const canResend =
                        shift.latest_stage === 'cancelled' ||
                        shift.latest_stage === 'declined' ||
                        shift.latest_stage === 'no_response';

                    if (
                        shiftIds.includes(shift.id) &&
                        (!shift.latest_confirmation_id || canResend)
                    ) {
                        // Only count if moving from not_started state
                        if (!shift.latest_confirmation_id) {
                            shiftsMovedToPending++;
                        }
                        return {
                            ...shift,
                            latest_stage: 'pending_confirmation' as ShiftStage,
                            latest_stage_created_at: new Date().toISOString(),
                            latest_confirmation_id: 'temp-' + shift.id,
                        };
                    }
                    return shift;
                });

            await optimisticallyUpdateShifts(updateShifts, (stats) =>
                stats
                    ? {
                          ...stats,
                          not_started: Math.max(
                              0,
                              stats.not_started - shiftsMovedToPending,
                          ),
                          pending: stats.pending + shiftsMovedToPending,
                      }
                    : stats,
            );
        },
        [optimisticallyUpdateShifts],
    );

    const handleSendIndividualConfirmation = useCallback(
        async (shift: Shift) => {
            if (!organizationId) {
                toast.error('Organization ID is required', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }

            posthog.capture(EVENTS.SHIFT_CONFIRMATION_SEND_ONE_CONFIRMATION, {
                organization_id: organizationId,
                user_id: user?.id,
                shift_id: shift.id,
                caregiver_id: shift.assigned_caregiver_id,
                shift_date: shift.shift_start_time,
            });

            if (!shift.assigned_caregiver_id) {
                toast.error('No caregiver assigned to this shift', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }

            if (!shift.caregiver) {
                toast.error('Caregiver information not available', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }

            // Capture previous state for rollback
            const previousStage = shift.latest_stage;
            const previousConfirmationId = shift.latest_confirmation_id;
            const previousStageCreatedAt = shift.latest_stage_created_at;

            // Optimistic update before API call
            await optimisticallyMarkShiftsPending([shift.id]);

            try {
                const result = await getApiClient().post({
                    url: '/organizations/{organization_id}/shift-confirmations/batch-create',
                    pathParams: { organization_id: organizationId },
                    body: {
                        shift_ids: [shift.id],
                    },
                });

                if (result.total_created > 0) {
                    toast.success(
                        `Confirmation sent for ${shift.patient_name || 'the'} shift`,
                        {
                            persistNotification: true,
                            notificationType: 'shift_action',
                        },
                    );
                } else if (
                    result.total_failed > 0 &&
                    result.failures.length > 0
                ) {
                    const error = result.failures[0]?.error;
                    if (error === CONFIRMATION_ALREADY_EXISTS_ERROR) {
                        toast.info(
                            'A confirmation has already been sent for this shift',
                            {
                                persistNotification: true,
                                notificationType: 'shift_action',
                            },
                        );
                    } else if (error === CAREGIVER_MISSING_PHONE_ERROR) {
                        toast.error(
                            'Failed to send confirmation because the assigned caregiver doesn’t have a phone number',
                            {
                                persistNotification: true,
                                notificationType: 'shift_action',
                            },
                        );
                        // Rollback on failure
                        await optimisticallyUpdateShifts(
                            (shifts) =>
                                shifts.map((s) =>
                                    s.id === shift.id
                                        ? {
                                              ...s,
                                              latest_stage: previousStage,
                                              latest_confirmation_id:
                                                  previousConfirmationId,
                                              latest_stage_created_at:
                                                  previousStageCreatedAt,
                                          }
                                        : s,
                                ),
                            (stats) =>
                                stats && !previousConfirmationId
                                    ? {
                                          ...stats,
                                          not_started: stats.not_started + 1,
                                          pending: Math.max(
                                              0,
                                              stats.pending - 1,
                                          ),
                                      }
                                    : stats,
                        );
                    } else {
                        toast.error(`Failed to send confirmation: ${error}`, {
                            persistNotification: true,
                            notificationType: 'shift_action',
                        });
                        // Rollback on failure
                        await optimisticallyUpdateShifts(
                            (shifts) =>
                                shifts.map((s) =>
                                    s.id === shift.id
                                        ? {
                                              ...s,
                                              latest_stage: previousStage,
                                              latest_confirmation_id:
                                                  previousConfirmationId,
                                              latest_stage_created_at:
                                                  previousStageCreatedAt,
                                          }
                                        : s,
                                ),
                            (stats) =>
                                stats && !previousConfirmationId
                                    ? {
                                          ...stats,
                                          not_started: stats.not_started + 1,
                                          pending: Math.max(
                                              0,
                                              stats.pending - 1,
                                          ),
                                      }
                                    : stats,
                        );
                    }
                } else {
                    toast.error('Failed to send confirmation', {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    });
                    // Rollback on failure
                    await optimisticallyUpdateShifts(
                        (shifts) =>
                            shifts.map((s) =>
                                s.id === shift.id
                                    ? {
                                          ...s,
                                          latest_stage: previousStage,
                                          latest_confirmation_id:
                                              previousConfirmationId,
                                          latest_stage_created_at:
                                              previousStageCreatedAt,
                                      }
                                    : s,
                            ),
                        (stats) =>
                            stats && !previousConfirmationId
                                ? {
                                      ...stats,
                                      not_started: stats.not_started + 1,
                                      pending: Math.max(0, stats.pending - 1),
                                  }
                                : stats,
                    );
                }
            } catch (error) {
                console.error('Error sending individual confirmation:', error);
                toast.error('Failed to send confirmation. Please try again.', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                // Rollback on error
                await optimisticallyUpdateShifts(
                    (shifts) =>
                        shifts.map((s) =>
                            s.id === shift.id
                                ? {
                                      ...s,
                                      latest_stage: previousStage,
                                      latest_confirmation_id:
                                          previousConfirmationId,
                                      latest_stage_created_at:
                                          previousStageCreatedAt,
                                  }
                                : s,
                        ),
                    (stats) =>
                        stats && !previousConfirmationId
                            ? {
                                  ...stats,
                                  not_started: stats.not_started + 1,
                                  pending: Math.max(0, stats.pending - 1),
                              }
                            : stats,
                );
            }
        },
        [
            organizationId,
            posthog,
            user,
            optimisticallyMarkShiftsPending,
            optimisticallyUpdateShifts,
        ],
    );

    const handleSendConfirmations = async (payload: SendPayload) => {
        if (!organizationId) {
            toast.error('Missing required data to send confirmations', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        let body:
            | { shift_ids: Array<string> }
            | { shift_start_date: string; shift_end_date: string };

        if (payload.mode === 'ids') {
            if (payload.ids.length === 0) {
                toast.error(
                    'No assigned shifts available to send confirmations',
                    {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    },
                );
                return;
            }
            body = { shift_ids: payload.ids };
        } else {
            body = {
                shift_start_date: payload.startIso,
                shift_end_date: payload.endIso,
            };
        }

        posthog.capture(EVENTS.SHIFT_CONFIRMATION_SEND_ALL_CONFIRMATIONS, {
            organization_id: organizationId,
            user_id: user?.id,
            selection_mode: payload.mode,
            requested_ids_count:
                payload.mode === 'ids' ? payload.ids.length : undefined,
        });

        setIsSendingConfirmations(true);

        // Capture previous state for rollback (only for ids mode)
        const previousShiftStates =
            payload.mode === 'ids'
                ? shifts
                      ?.filter((s) => payload.ids.includes(s.id))
                      .map((s) => ({
                          id: s.id,
                          latest_stage: s.latest_stage,
                          latest_confirmation_id: s.latest_confirmation_id,
                          latest_stage_created_at: s.latest_stage_created_at,
                      }))
                : undefined;

        // Optimistic update before API call (only for ids mode)
        if (payload.mode === 'ids') {
            await optimisticallyMarkShiftsPending(payload.ids);
        }

        try {
            const result = await getApiClient().post({
                url: '/organizations/{organization_id}/shift-confirmations/batch-create',
                pathParams: { organization_id: organizationId },
                body,
            });

            setIsConfirmModalOpen(false);

            // Only revalidate for date range mode (we don't know which shifts were affected)
            // Don't refetch for "already exists" failures - those are expected
            const hasRealFailures = result.failures?.some(
                (failure) =>
                    failure.error !== CONFIRMATION_ALREADY_EXISTS_ERROR,
            );

            if (payload.mode === 'date_range' || hasRealFailures) {
                await reloadShifts();
            }

            const realFailures = result.failures.filter(
                (failure) =>
                    failure.error !== CONFIRMATION_ALREADY_EXISTS_ERROR,
            );
            const alreadyExistsCount =
                result.total_failed - realFailures.length;
            const missingPhoneError = getMissingPhoneConfirmationError(
                result.total_created,
                result.failures,
            );
            if (missingPhoneError) {
                toast.error(missingPhoneError, {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            } else if (result.total_created > 0) {
                let message = `Successfully sent ${result.total_created} confirmation${
                    result.total_created === 1 ? '' : 's'
                }`;

                if (realFailures.length > 0) {
                    message += `. ${realFailures.length} failed to send.`;
                } else if (alreadyExistsCount > 0) {
                    message += `. ${alreadyExistsCount} already had confirmations.`;
                } else {
                    message += '!';
                }

                toast.success(message, {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            } else if (alreadyExistsCount > 0) {
                toast.info(
                    `All ${alreadyExistsCount} selected shift${alreadyExistsCount === 1 ? '' : 's'} already had confirmations sent.`,
                    {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    },
                );
            } else {
                toast.info('No new confirmations were sent.', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            }

            if (realFailures.length > 0) {
                console.error(
                    'Failed confirmations (real failures):',
                    realFailures,
                );
            }
        } catch (error) {
            console.error('Error sending confirmations:', error);
            toast.error('Failed to send confirmations. Please try again.', {
                persistNotification: true,
                notificationType: 'shift_action',
            });

            // Rollback optimistic update on error (only for ids mode)
            if (payload.mode === 'ids' && previousShiftStates) {
                const previousMap = new Map(
                    previousShiftStates.map((s) => [s.id, s]),
                );
                const shiftsWithoutConfirmation = previousShiftStates.filter(
                    (s) => !s.latest_confirmation_id,
                ).length;

                await optimisticallyUpdateShifts(
                    (shifts) =>
                        shifts.map((s) => {
                            const prev = previousMap.get(s.id);
                            if (prev) {
                                return {
                                    ...s,
                                    latest_stage: prev.latest_stage,
                                    latest_confirmation_id:
                                        prev.latest_confirmation_id,
                                    latest_stage_created_at:
                                        prev.latest_stage_created_at,
                                };
                            }
                            return s;
                        }),
                    (stats) =>
                        stats && shiftsWithoutConfirmation > 0
                            ? {
                                  ...stats,
                                  not_started:
                                      stats.not_started +
                                      shiftsWithoutConfirmation,
                                  pending: Math.max(
                                      0,
                                      stats.pending - shiftsWithoutConfirmation,
                                  ),
                              }
                            : stats,
                );
            }
        } finally {
            setIsSendingConfirmations(false);
        }
    };

    const handleSyncShifts = async () => {
        if (!organizationId) {
            toast.error('Organization ID not found', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        if (!hasSyncEnabledAccountForShift) {
            toast.error('EHR syncing is not enabled for this organization.', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        posthog.capture(EVENTS.SYNC_SHIFTS_TRIGGERED, {
            organization_id: organizationId,
            user_id: user?.id,
        });

        await runSyncWithFeedback({
            setIsSyncing,
            successMessage: 'Shift sync queued',
            trigger: () =>
                getApiClient().post({
                    url: '/organizations/{organization_id}/trigger-sync',
                    pathParams: { organization_id: organizationId },
                    queryParams: {
                        entity_type: 'shifts',
                        ...(selectedDateRange.from && {
                            sync_date: selectedDateRange.from,
                        }),
                    },
                }),
        });
    };

    const handleSyncSelectedShift = useCallback(async () => {
        if (!selectedShift) {
            return;
        }

        const organizationIdForShift =
            selectedShift.organization_id ?? organizationId;

        if (!organizationIdForShift) {
            toast.error('Organization ID not found', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        if (!hasSyncEnabledAccountForShift) {
            toast.error('Syncing is not enabled for this organization.', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        setIsSyncingSelectedShift(true);
        pendingSelectedShiftSyncIdRef.current = selectedShift.id;

        let syncSucceeded = false;
        try {
            await getApiClient().post({
                url: '/organizations/{organization_id}/shifts/{shift_id}/sync',
                pathParams: {
                    organization_id: organizationIdForShift,
                    shift_id: selectedShift.id,
                },
            });
            syncSucceeded = true;
        } catch (error) {
            console.error('Error triggering shift sync:', error);

            if (error instanceof ApiError && error.status === 429) {
                toast.error(
                    'Rate limit exceeded. Please try again in a few minutes.',
                    {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    },
                );
            } else {
                toast.error('Failed to start shift sync. Please try again.', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            }
        }

        if (syncSucceeded) {
            toast.success('Shift synced', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            try {
                await Promise.all([
                    mutateSelectedShiftDetails(),
                    reloadShifts(),
                ]);
            } catch (refreshError) {
                console.error(
                    'Failed to refresh shift after sync:',
                    refreshError,
                );
            }
        }

        pendingSelectedShiftSyncIdRef.current = null;
        setIsSyncingSelectedShift(false);
    }, [
        organizationId,
        selectedShift,
        hasSyncEnabledAccountForShift,
        mutateSelectedShiftDetails,
        reloadShifts,
    ]);

    const handleCreateCallout = useCallback((shift: Shift) => {
        setSelectedShiftIdsForOutreach(new Set([shift.id]));
        setShowCreateOutreachModal(true);
    }, []);

    // Client lookup for the selection cap: same-client selections get the
    // higher cap. Ids that no longer resolve (e.g. after a filter change)
    // map to null and conservatively keep the mixed-client cap.
    const clientIdByShiftId = useMemo(
        () =>
            new Map(shifts.map((shift) => [shift.id, shift.client_id ?? null])),
        [shifts],
    );

    const outreachSelectionLimit = useMemo(
        () =>
            multiShiftOutreachMax(
                [...selectedShiftIdsForOutreach].map(
                    (id) => clientIdByShiftId.get(id) ?? null,
                ),
            ),
        [selectedShiftIdsForOutreach, clientIdByShiftId],
    );

    const handleToggleShiftForOutreach = useCallback(
        (shiftId: string) => {
            setSelectedShiftIdsForOutreach((prev) => {
                const next = new Set(prev);
                if (next.has(shiftId)) {
                    next.delete(shiftId);
                    return next;
                }
                const selectionLimit = multiShiftOutreachMax(
                    [...next, shiftId].map(
                        (id) => clientIdByShiftId.get(id) ?? null,
                    ),
                );
                if (next.size < selectionLimit) {
                    next.add(shiftId);
                }
                return next;
            });
        },
        [clientIdByShiftId],
    );

    const handleClearShiftSelection = useCallback(() => {
        setSelectedShiftIdsForOutreach(new Set());
    }, []);

    const selectedShiftIdsForOutreachArray = useMemo(
        () =>
            selectedShiftIdsForOutreach.size > 0
                ? [...selectedShiftIdsForOutreach]
                : undefined,
        [selectedShiftIdsForOutreach],
    );

    const handleCreateOutreachFromSelection = useCallback(() => {
        if (selectedShiftIdsForOutreach.size === 0) {
            return;
        }
        setShowCreateOutreachModal(true);
    }, [selectedShiftIdsForOutreach.size]);

    useEffect(() => {
        if (!calloutShiftId) {
            handledCreateCalloutShiftIdRef.current = null;
            return;
        }
        if (handledCreateCalloutShiftIdRef.current === calloutShiftId) {
            return;
        }

        handledCreateCalloutShiftIdRef.current = calloutShiftId;
        setSelectedShiftIdsForOutreach(new Set([calloutShiftId]));
        setShowCreateOutreachModal(true);
    }, [calloutShiftId]);

    const handleUpdateConfirmationStatus = useCallback(
        async (shift: Shift, status: ConfirmationStatus) => {
            const organizationId = shift.organization_id;

            if (!shift.assigned_caregiver_id) {
                toast.error('No caregiver assigned to this shift', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }

            // Capture previous state for rollback
            const previousStage = shift.latest_stage;

            const nextStage: ShiftStage = status;

            const isAssignedStage = (
                stage: ShiftStage | null | undefined,
            ): boolean => stage === 'assigned';
            const isPendingStage = (
                stage: ShiftStage | null | undefined,
            ): boolean =>
                stage === 'pending_confirmation' ||
                stage === 'confirmation_follow_up';
            const isConfirmedStage = (
                stage: ShiftStage | null | undefined,
            ): boolean => stage === 'confirmed';
            const isCancelledStage = (
                stage: ShiftStage | null | undefined,
            ): boolean => stage === 'cancelled' || stage === 'declined';

            const updateStatsForStageChange = (
                stats: ShiftStatsData,
                fromStage: ShiftStage | null | undefined,
                toStage: ShiftStage | null | undefined,
            ): ShiftStatsData => {
                const total =
                    stats.not_started +
                    stats.pending +
                    stats.confirmed +
                    stats.other;
                let notStarted = stats.not_started;
                let pending = stats.pending;
                let confirmed = stats.confirmed;
                let cancelled = stats.cancelled;

                if (isAssignedStage(fromStage)) {
                    notStarted = Math.max(0, notStarted - 1);
                } else if (isPendingStage(fromStage)) {
                    pending = Math.max(0, pending - 1);
                } else if (isConfirmedStage(fromStage)) {
                    confirmed = Math.max(0, confirmed - 1);
                } else if (isCancelledStage(fromStage)) {
                    cancelled = Math.max(0, cancelled - 1);
                }

                if (isAssignedStage(toStage)) {
                    notStarted += 1;
                } else if (isPendingStage(toStage)) {
                    pending += 1;
                } else if (isConfirmedStage(toStage)) {
                    confirmed += 1;
                } else if (isCancelledStage(toStage)) {
                    cancelled += 1;
                }

                const other = Math.max(
                    0,
                    total - notStarted - pending - confirmed,
                );

                return {
                    ...stats,
                    not_started: notStarted,
                    pending,
                    confirmed,
                    cancelled,
                    other,
                };
            };

            // Optimistic update before API call
            await optimisticallyUpdateShifts(
                (shifts) =>
                    shifts.map((s) =>
                        s.id === shift.id
                            ? {
                                  ...s,
                                  latest_stage: status as ShiftStage,
                              }
                            : s,
                    ),
                (stats) =>
                    stats
                        ? updateStatsForStageChange(
                              stats,
                              previousStage,
                              nextStage,
                          )
                        : stats,
            );

            try {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/shifts/{shift_id}/update-stage',
                    pathParams: {
                        organization_id: organizationId,
                        shift_id: shift.id,
                    },
                    body: {
                        stage: nextStage,
                        caregiver_id: shift.assigned_caregiver_id,
                    },
                });

                toast.success(`Shift marked as ${status}`, {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            } catch (error) {
                console.error(`Error marking shift as ${status}:`, error);
                const detail =
                    error instanceof ApiError
                        ? (error.body as { detail?: string })?.detail
                        : null;
                toast.error(detail ?? `Failed to mark shift as ${status}`, {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });

                // Rollback on error - restore the previous bucket counts
                await optimisticallyUpdateShifts(
                    (shifts) =>
                        shifts.map((s) =>
                            s.id === shift.id
                                ? { ...s, latest_stage: previousStage }
                                : s,
                        ),
                    (stats) =>
                        stats
                            ? updateStatsForStageChange(
                                  stats,
                                  nextStage,
                                  previousStage,
                              )
                            : stats,
                );
            }
        },
        [optimisticallyUpdateShifts],
    );

    const handleUpdateShiftStage = useCallback(
        async (shift: Shift, stage: ShiftStage) => {
            if (!organizationId) {
                toast.error('Organization ID not found', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }

            if (!shift.assigned_caregiver_id) {
                toast.error('No caregiver assigned to this shift', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }

            const previousStage = shift.latest_stage;

            try {
                // Optimistic update
                await optimisticallyUpdateShifts((shifts) =>
                    shifts.map((s) =>
                        s.id === shift.id ? { ...s, latest_stage: stage } : s,
                    ),
                );

                await getApiClient().post({
                    url: '/organizations/{organization_id}/shifts/{shift_id}/update-stage',
                    pathParams: {
                        organization_id: organizationId,
                        shift_id: shift.id,
                    },
                    body: {
                        stage,
                        caregiver_id: shift.assigned_caregiver_id,
                    },
                });

                const stageLabel =
                    stage === 'clocked_in' ? 'clocked in' : 'clocked out';
                toast.success(`Shift ${stageLabel} successfully`, {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            } catch (error) {
                console.error('Error updating shift stage:', error);
                const detail =
                    error instanceof ApiError
                        ? (error.body as { detail?: string })?.detail
                        : null;
                toast.error(detail ?? 'Failed to update shift stage', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });

                // Restore previous state
                await optimisticallyUpdateShifts((shifts) =>
                    shifts.map((s) =>
                        s.id === shift.id
                            ? { ...s, latest_stage: previousStage }
                            : s,
                    ),
                );
            }
        },
        [organizationId, optimisticallyUpdateShifts],
    );

    const handleSetCaregiver = async (
        shift: Shift,
        caregiverId: string,
        ehrName?: string,
    ) => {
        if (!organizationId) {
            toast.error('Organization ID not found', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        try {
            const updateData: { assigned_caregiver_id?: string | null } = {};

            if (caregiverId === 'unassigned') {
                updateData.assigned_caregiver_id = null;
            } else {
                updateData.assigned_caregiver_id = caregiverId;
            }

            const updateResponse = await getApiClient().patch({
                url: '/organizations/{organization_id}/shifts/{shift_id}',
                pathParams: {
                    organization_id: organizationId,
                    shift_id: shift.id,
                },
                body: updateData,
            });

            await reloadShifts();
            await mutateSelectedShiftDetails();
            showCaregiverWritebackToast(caregiverId, updateResponse, ehrName);
        } catch (error) {
            console.error('Error updating caregiver assignment:', error);
            toast.error('Failed to update caregiver assignment', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
        }
    };
    const handleToday = useCallback(() => {
        const todayKey = toDateKey(new Date(), timezone);
        const range = { from: todayKey, to: todayKey };
        setSelectedDateRange(range);

        navigate({
            to: selectedShift ? '/shifts/$shift-id' : '/shifts',
            params: selectedShift
                ? { 'shift-id': selectedShift.id }
                : undefined,
            search: {
                ...currentShiftRouteSearch,
                from: range.from,
                to: range.to,
            },
        });
    }, [currentShiftRouteSearch, navigate, selectedShift, timezone]);

    const handleDateRangeChange = useCallback(
        (range: DateRangePickerValue) => {
            setSelectedDateRange(range);

            navigate({
                to: selectedShift ? '/shifts/$shift-id' : '/shifts',
                params: selectedShift
                    ? { 'shift-id': selectedShift.id }
                    : undefined,
                search: {
                    ...currentShiftRouteSearch,
                    from: range.from,
                    to: range.to,
                },
            });
        },
        [currentShiftRouteSearch, navigate, selectedShift],
    );

    const handleDateRangeOffset = useCallback(
        (days: number) => {
            handleDateRangeChange({
                from: addDaysToDateKey(selectedDateRange.from, days),
                to: addDaysToDateKey(selectedDateRange.to, days),
            });
        },
        [handleDateRangeChange, selectedDateRange],
    );

    const handleCloseShiftDialog = useCallback(() => {
        setSelectedShift(null);
        navigate({
            to: '/shifts',
            search: (prev) => {
                const { shiftId, ...rest } = prev as Record<string, unknown>;
                return {
                    ...rest,
                    from: selectedDateRange.from,
                    to: selectedDateRange.to,
                };
            },
            replace: false,
        });
    }, [navigate, selectedDateRange]);

    const handleNavigateToOutreach = useCallback(
        (outreachId: string) => {
            setSelectedShift(null);
            navigate({
                to: '/outreaches/$callout-id',
                params: { 'callout-id': outreachId },
            });
        },
        [navigate],
    );

    const openConfirmationModal = useCallback((payload: SendPayload) => {
        setSendPayload(payload);
        setIsConfirmModalOpen(true);
    }, []);

    const openPendingMessagesDialog = useCallback(() => {
        void navigate({
            to: '/shifts',
            search: { ...currentShiftRouteSearch, approve: true },
        });
    }, [navigate, currentShiftRouteSearch]);

    const closePendingMessagesDialog = useCallback(
        (open: boolean) => {
            if (!open) {
                void navigate({
                    to: '/shifts',
                    search: {
                        ...currentShiftRouteSearch,
                        approve: undefined,
                    },
                });
            }
        },
        [navigate, currentShiftRouteSearch],
    );

    const openSandboxSetupModal = useCallback(() => {
        setShowSandboxSetupModal(true);
    }, []);

    const openCreateTestShiftModal = useCallback(() => {
        setShowCreateTestShiftModal(true);
    }, []);

    const primaryDataSourceType =
        dataSourceAccounts && dataSourceAccounts.length > 0
            ? dataSourceAccounts[0]!.type
            : null;

    // Memoize columns to prevent unnecessary re-renders
    const columns = useMemo(() => {
        const baseColumns = createColumns({
            handleRowClick,
            timezone,
            onDeleteConfirmation: handleDeleteShiftConfirmation,
            onSendConfirmation: handleSendIndividualConfirmation,
            onUpdateConfirmationStatus: handleUpdateConfirmationStatus,
            onCreateCallout: handleCreateCallout,
            calloutStatusMap,
            displayCaregiverNamesLastFirst,
            sortByLastName,
            primaryDataSourceType,
            allowClockInWrite: Boolean(
                organization?.features?.['allow_clock_in_write'],
            ),
            allowClockOutWrite: Boolean(
                organization?.features?.['allow_clock_out_write'],
            ),
            onUpdateShiftStage: handleUpdateShiftStage,
            getBlockedCaregiverInfos,
            onCaregiverUnblocked: () => mutateBlockedCaregivers(),
            careCoordinatorSupportEnabled,
            clientReferenceTerm,
            clientReferenceTermTitle,
            showDateColumn: selectedDateRange.from !== selectedDateRange.to,
        });

        const checkboxColumn: ColumnDef<Shift> = {
            id: 'multi_shift_select',
            size: 40,
            header: () => null,
            cell: ({ row }) => {
                const isSelected = selectedShiftIdsForOutreach.has(
                    row.original.id,
                );
                // Cap the selection would have with this row added:
                // same-client rows stay selectable up to the higher cap.
                const candidateLimit = multiShiftOutreachMax(
                    [...selectedShiftIdsForOutreach, row.original.id].map(
                        (id) => clientIdByShiftId.get(id) ?? null,
                    ),
                );
                const isAtLimit =
                    selectedShiftIdsForOutreach.size >= candidateLimit;
                const shiftEndTime = new Date(row.original.shift_end_time);
                if (shiftEndTime < new Date()) {
                    return null;
                }
                const isDisabled = !isSelected && isAtLimit;
                return (
                    <div
                        className="flex items-center justify-center"
                        data-row-click-ignore={true}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={() =>
                                handleToggleShiftForOutreach(row.original.id)
                            }
                            aria-label="Select shift for outreach"
                            className="[&_svg]:stroke-[2.6]"
                        />
                    </div>
                );
            },
            enableSorting: false,
        };

        return [checkboxColumn, ...baseColumns];
    }, [
        handleRowClick,
        timezone,
        handleDeleteShiftConfirmation,
        handleSendIndividualConfirmation,
        handleUpdateConfirmationStatus,
        handleCreateCallout,
        handleUpdateShiftStage,
        calloutStatusMap,
        displayCaregiverNamesLastFirst,
        sortByLastName,
        primaryDataSourceType,
        organization?.features,
        getBlockedCaregiverInfos,
        mutateBlockedCaregivers,
        careCoordinatorSupportEnabled,
        clientReferenceTerm,
        clientReferenceTermTitle,
        selectedDateRange.from,
        selectedDateRange.to,
        selectedShiftIdsForOutreach,
        handleToggleShiftForOutreach,
        clientIdByShiftId,
    ]);

    return (
        <React.Fragment>
            <div className="flex h-full min-h-0 overflow-hidden max-md:h-[calc(100svh-3.5rem)]">
                {/* The pane can be squeezed by the chat panel, so
                    layout-critical children use @container variants
                    against the pane width, not the viewport. */}
                <div className="@container flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-4 pb-4 max-md:px-3 max-md:pb-3">
                    <ShiftListHeader
                        selectedDateRange={selectedDateRange}
                        timezone={timezone}
                        weekStartsOn={
                            WEEKDAY_INDEX_BY_DAY[
                                organization?.general_settings
                                    ?.work_week_start_day ?? 'monday'
                            ]
                        }
                        shiftCount={shiftCountForHeader}
                        shiftHours={shiftHoursForHeader}
                        isShiftCountLoading={isLoadingSearch}
                        lastFetched={lastFetched}
                        isTodaySelected={isTodaySelected}
                        onToday={handleToday}
                        onDateRangeChange={handleDateRangeChange}
                        onPreviousDateRange={() => handleDateRangeOffset(-1)}
                        onNextDateRange={() => handleDateRangeOffset(1)}
                    />
                    <ShiftStatsGrid stats={stats} isLoading={isLoadingBase} />
                    <div
                        className="relative min-h-0 flex-1 overflow-hidden"
                        data-testid="shifts-table"
                    >
                        <ShiftListTableSection
                            isLoading={isLoadingSearch}
                            error={error}
                            displayMode={displayMode}
                            shifts={shifts}
                            columns={columns}
                            getRowNavigation={getShiftNavigation}
                            columnVisibilityKey={columnVisibilityKey}
                            initialColumnVisibility={initialColumnVisibility}
                            columnFilters={columnFilters}
                            filterKeyPrefix={FILTER_KEY_PREFIX}
                            totalShifts={totalShifts}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onColumnFiltersChange={updateColumnFilters}
                            toolbarProps={{
                                shifts,
                                allAssignedShiftIds,
                                onSyncShifts: handleSyncShifts,
                                isSyncing,
                                hasSyncEnabledAccount:
                                    hasSyncEnabledAccountForShift,
                                pendingMessagesCount: pendingMessagesCount ?? 0,
                                isLoadingPendingMessagesCount,
                                onShowPendingMessages:
                                    openPendingMessagesDialog,
                                mode,
                                clockInOutEnabled,
                                createShiftEnabled,
                                onShowSandboxSetupModal: openSandboxSetupModal,
                                onShowCreateTestShiftModal:
                                    openCreateTestShiftModal,
                                onRequestSendConfirmations:
                                    openConfirmationModal,
                                isSendingConfirmations,
                                isSelectedDateInPast,
                                shiftStartIso,
                                shiftEndIso,
                                isLoading: isLoadingSearch,
                                searchQuery,
                                onSearchChange: setSearchQuery,
                                serverSideGlobalFilter: shouldUseServerSearch,
                                uniqueOffices: offices,
                                uniqueTags: tags,
                                myCareCoordinator: myCareCoordinator ?? null,
                                careCoordinatorSupportEnabled,
                                careCoordinators,
                                careCoordinatorFilter,
                                onSelectCoordinator: handleSelectCoordinator,
                                onShowAllCoordinators:
                                    handleShowAllCoordinators,
                                clients: filterClients,
                                clientFilterIds,
                                onSelectClient: toggleClientFilter,
                                onShowAllClients: () => updateClientFilter([]),
                                clientReferenceTerm,
                            }}
                            displayCaregiverNamesLastFirst={
                                displayCaregiverNamesLastFirst
                            }
                        />
                        <MultiShiftSelectionBar
                            selectedCount={selectedShiftIdsForOutreach.size}
                            maxCount={outreachSelectionLimit}
                            onClear={handleClearShiftSelection}
                            onCreateOutreach={handleCreateOutreachFromSelection}
                        />
                    </div>
                </div>
            </div>
            <ShiftDetailsDialog
                // SWR-fresh detail data is the source of truth when
                // loaded; the click-time row is the fallback during
                // initial fetch. This way the dialog re-renders on any
                // revalidation (sync, caregiver assignment, websocket
                // updates, periodic refresh) without per-handler state
                // synchronization.
                selectedShift={selectedShiftDetails ?? selectedShift}
                selectedShiftDetails={selectedShiftDetails ?? null}
                timezone={timezone}
                displayCaregiverNamesLastFirst={displayCaregiverNamesLastFirst}
                caregiverUnassignmentEnabled={isEhrCaregiverUnassignmentWritebackEnabled(
                    organization?.features,
                )}
                caregivers={caregivers}
                caregiverForm={caregiverForm}
                mode={mode}
                organizationId={organizationId}
                hasActiveCallout={hasActiveCalloutForSelectedShift}
                activeCallout={activeCalloutForSelectedShift}
                activeClockReminder={activeClockReminderForSelectedShift}
                hasWellSkyAccount={hasWellSkyAccount}
                hasSyncEnabledAccountForShift={hasSyncEnabledAccountForShift}
                dataSourceAccounts={dataSourceAccounts}
                onSyncSelectedShift={handleSyncSelectedShift}
                isSyncingSelectedShift={isSyncingSelectedShift}
                onCreateCallout={handleCreateCallout}
                onSetCaregiver={handleSetCaregiver}
                onClose={handleCloseShiftDialog}
                onNavigateToOutreach={handleNavigateToOutreach}
                userIsAdmin={Boolean(user?.admin)}
                allCareCoordinators={careCoordinators}
                onShiftUpdated={mutateSelectedShiftDetails}
                clientReferenceTerm={clientReferenceTerm}
                clientReferenceTermTitle={clientReferenceTermTitle}
            />
            {/* Confirmation Modal */}
            <SendConfirmationsDialog
                isOpen={isConfirmModalOpen}
                isSending={isSendingConfirmations}
                sendPayload={sendPayload}
                selectedDateRange={selectedDateRange}
                timezone={timezone}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleSendConfirmations}
            />
            <CreateOutreachModal
                isOpen={showCreateOutreachModal}
                shiftIds={selectedShiftIdsForOutreachArray}
                onClose={() => {
                    setShowCreateOutreachModal(false);
                    if (currentSearch.calloutShiftId) {
                        void navigate({
                            to: '/shifts',
                            search: {
                                ...currentShiftRouteSearch,
                                calloutShiftId: undefined,
                            },
                            replace: true,
                        });
                    }
                }}
                onSuccess={() => {
                    handleClearShiftSelection();
                }}
            />
            <PendingMessagesDialog
                organizationId={organizationId!}
                open={showApproveModal ?? false}
                onOpenChange={closePendingMessagesDialog}
                domain="shift_confirmation"
                careCoordinatorIds={
                    careCoordinatorSupportEnabled
                        ? careCoordinatorFilterIdsForSync
                        : undefined
                }
            />
            <SandboxSetupModal
                open={showSandboxSetupModal}
                onOpenChange={setShowSandboxSetupModal}
                onComplete={() => {
                    reloadShifts();
                    setShowSandboxSetupModal(false);
                }}
            />
            <CreateShiftModal
                open={showCreateTestShiftModal}
                onOpenChange={setShowCreateTestShiftModal}
                onComplete={() => {
                    reloadShifts();
                    setShowCreateTestShiftModal(false);
                }}
                initialDateKey={selectedDateRange.from}
            />
        </React.Fragment>
    );
}

type ShiftListHeaderProps = {
    readonly selectedDateRange: DateRangePickerValue;
    readonly timezone: string;
    readonly weekStartsOn: WeekdayIndex;
    readonly shiftCount: number;
    readonly shiftHours: number;
    readonly isShiftCountLoading: boolean;
    readonly lastFetched: string | null;
    readonly isTodaySelected: boolean;
    readonly onToday: () => void;
    readonly onDateRangeChange: (value: DateRangePickerValue) => void;
    readonly onPreviousDateRange: () => void;
    readonly onNextDateRange: () => void;
};

function ShiftListHeader({
    selectedDateRange,
    timezone,
    weekStartsOn,
    shiftCount,
    shiftHours,
    isShiftCountLoading,
    lastFetched,
    isTodaySelected,
    onToday,
    onDateRangeChange,
    onPreviousDateRange,
    onNextDateRange,
}: ShiftListHeaderProps) {
    const isSingleDay = selectedDateRange.from === selectedDateRange.to;
    const shiftLabel = shiftCount === 1 ? 'shift' : 'shifts';
    const formattedHours = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
    }).format(shiftHours);
    const hourLabel = shiftHours === 1 ? 'hour' : 'hours';
    const titleLabel = isSingleDay
        ? formatDateKeyLong(selectedDateRange.from, timezone)
        : formatDateKeyRange(
              selectedDateRange.from,
              selectedDateRange.to,
              timezone,
          );
    const shiftCountText =
        isSingleDay && isTodaySelected
            ? `${shiftCount} ${shiftLabel} · ${formattedHours} ${hourLabel} today`
            : `${shiftCount} ${shiftLabel} · ${formattedHours} ${hourLabel}`;

    return (
        <div className="my-2 mb-2 flex shrink-0 flex-col gap-2 md:my-4 md:mb-9 @2xl:flex-row @2xl:items-center @2xl:justify-between">
            <div>
                <h2 className="text-lg leading-6 font-semibold md:text-xl md:leading-normal">
                    Shifts for {titleLabel}
                </h2>
                <div className="text-muted-foreground mt-0.5 text-sm md:mt-1">
                    {isShiftCountLoading ? (
                        <Skeleton className="h-4 w-24" />
                    ) : (
                        shiftCountText
                    )}
                </div>
                {lastFetched && (
                    <p className="text-muted-foreground mt-0.5 text-xs md:mt-1">
                        Shifts last synced: {lastFetched}
                    </p>
                )}
            </div>
            <div
                className="flex flex-wrap items-center gap-1.5 md:gap-2"
                data-testid="shifts-date-controls"
            >
                <TrainingModePageTourReplayButton />
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onToday}
                    disabled={isTodaySelected}
                >
                    Today
                </Button>
                <Button
                    variant="outline"
                    size="icon"
                    onClick={onPreviousDateRange}
                    aria-label="Previous date range"
                    className="size-9 shrink-0"
                >
                    <ChevronLeft className="size-4" />
                </Button>
                <DateRangePicker
                    value={selectedDateRange}
                    timezone={timezone}
                    direction="forward"
                    weekStartsOn={weekStartsOn}
                    onChange={onDateRangeChange}
                    className="min-w-0 flex-1 @2xl:w-[240px] @2xl:flex-none"
                />
                <Button
                    variant="outline"
                    size="icon"
                    onClick={onNextDateRange}
                    aria-label="Next date range"
                    className="size-9 shrink-0"
                >
                    <ChevronRight className="size-4" />
                </Button>
            </div>
        </div>
    );
}

type ShiftStatsGridProps = {
    readonly stats: ShiftStatsData;
    readonly isLoading: boolean;
};

function ShiftStatsGrid({ stats, isLoading }: ShiftStatsGridProps) {
    const totalShiftConfirmationsSent = stats.pending + stats.confirmed;
    const statCardClassName = 'gap-1 py-1.5 md:gap-6 md:py-6';
    const statHeaderClassName = 'gap-0.5 px-1.5 md:gap-1 md:px-6';
    const statTitleClassName =
        'text-base leading-5 font-bold md:text-3xl md:leading-none';
    const statDescriptionClassName = 'text-[11px] leading-3 md:text-sm';

    return (
        <div
            className="mb-2 grid shrink-0 grid-cols-4 gap-1 md:mb-4 md:gap-4"
            data-testid="shifts-stats"
        >
            <Card className={statCardClassName}>
                <CardHeader className={statHeaderClassName}>
                    <CardTitle className={statTitleClassName}>
                        {isLoading ? (
                            <Skeleton className="h-5 w-10 md:h-9 md:w-16" />
                        ) : (
                            stats.not_started
                        )}
                    </CardTitle>
                    <CardDescription className={statDescriptionClassName}>
                        {isLoading ? (
                            <Skeleton className="h-4 w-24" />
                        ) : (
                            <React.Fragment>
                                <span className="@[10rem]:hidden">
                                    Not Started
                                </span>
                                <span className="hidden @[10rem]:inline">
                                    Not Yet Started
                                </span>
                            </React.Fragment>
                        )}
                    </CardDescription>
                </CardHeader>
            </Card>
            <Card className={statCardClassName}>
                <CardHeader className={statHeaderClassName}>
                    <CardTitle className={statTitleClassName}>
                        {isLoading ? (
                            <Skeleton className="h-5 w-10 md:h-9 md:w-16" />
                        ) : (
                            stats.pending
                        )}
                    </CardTitle>
                    <CardDescription className={statDescriptionClassName}>
                        {isLoading ? (
                            <Skeleton className="h-4 w-32" />
                        ) : (
                            <React.Fragment>
                                <span className="@[10rem]:hidden">Waiting</span>
                                <span className="hidden @[10rem]:inline">
                                    Waiting for Response
                                </span>
                            </React.Fragment>
                        )}
                    </CardDescription>
                </CardHeader>
            </Card>
            <Card className={statCardClassName}>
                <CardHeader className={statHeaderClassName}>
                    <CardTitle className={statTitleClassName}>
                        {isLoading ? (
                            <Skeleton className="h-5 w-10 md:h-9 md:w-16" />
                        ) : (
                            stats.confirmed
                        )}
                    </CardTitle>
                    <CardDescription className={statDescriptionClassName}>
                        {isLoading ? (
                            <Skeleton className="h-4 w-20" />
                        ) : (
                            'Confirmed'
                        )}
                    </CardDescription>
                    {!isLoading && totalShiftConfirmationsSent > 0 && (
                        <CardDescription className="hidden text-[11px] leading-3 md:text-xs md:leading-4 @[10rem]:block">
                            {`${totalShiftConfirmationsSent} total shift confirmations sent`}
                        </CardDescription>
                    )}
                </CardHeader>
            </Card>
            <Card className={statCardClassName}>
                <CardHeader className={statHeaderClassName}>
                    <CardTitle className={statTitleClassName}>
                        {isLoading ? (
                            <Skeleton className="h-5 w-10 md:h-9 md:w-16" />
                        ) : (
                            stats.other
                        )}
                    </CardTitle>
                    <CardDescription className={statDescriptionClassName}>
                        {isLoading ? (
                            <Skeleton className="h-4 w-20" />
                        ) : (
                            <React.Fragment>
                                <span className="@[10rem]:hidden">Other</span>
                                <span className="hidden @[10rem]:inline">
                                    Archived
                                </span>
                            </React.Fragment>
                        )}
                    </CardDescription>
                    <CardDescription
                        className={cn(
                            statDescriptionClassName,
                            'hidden @[10rem]:block',
                        )}
                    >
                        {isLoading ? (
                            <Skeleton className="h-4 w-20" />
                        ) : (
                            'Other'
                        )}
                    </CardDescription>
                </CardHeader>
            </Card>
        </div>
    );
}

type ShiftDisplayToggleProps = {
    readonly displayMode: DisplayMode;
    readonly onDisplayModeChange: (mode: DisplayMode) => void;
};

export function ShiftDisplayToggle({
    displayMode,
    onDisplayModeChange,
}: ShiftDisplayToggleProps) {
    return (
        <div className="mb-4 flex justify-end">
            <DropdownMenu>
                <DropdownMenuTrigger asChild={true}>
                    <Button variant="outline" size="sm" className="w-auto">
                        <LayoutGrid className="mr-2 h-4 w-4" />
                        Display
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        onClick={() => onDisplayModeChange('grouped')}
                    >
                        <div className="flex w-full items-center justify-between">
                            <span>Group by Shift Stage</span>
                            {displayMode === 'grouped' && (
                                <Check className="ml-2 h-4 w-4" />
                            )}
                        </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => onDisplayModeChange('ungrouped')}
                    >
                        <div className="flex w-full items-center justify-between">
                            <span>Single Table</span>
                            {displayMode === 'ungrouped' && (
                                <Check className="ml-2 h-4 w-4" />
                            )}
                        </div>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

type ShiftListToolbarBaseProps = {
    readonly shifts: Array<Shift>;
    readonly allAssignedShiftIds: Array<string>;
    readonly onSyncShifts: () => void;
    readonly isSyncing: boolean;
    readonly hasSyncEnabledAccount: boolean;
    readonly pendingMessagesCount: number;
    readonly isLoadingPendingMessagesCount: boolean;
    readonly onShowPendingMessages: () => void;
    readonly mode: Mode;
    readonly clockInOutEnabled: boolean;
    readonly createShiftEnabled: boolean;
    readonly onShowSandboxSetupModal: () => void;
    readonly onShowCreateTestShiftModal: () => void;
    readonly onRequestSendConfirmations: (payload: SendPayload) => void;
    readonly isSendingConfirmations: boolean;
    readonly isSelectedDateInPast: boolean;
    readonly shiftStartIso?: string;
    readonly shiftEndIso?: string;
    readonly isLoading: boolean;
    readonly searchQuery: string;
    readonly onSearchChange: (value: string) => void;
    /** When true, skip client-side global filtering (server handles it) */
    readonly serverSideGlobalFilter: boolean;
    // Unique office names for filter dropdown
    readonly uniqueOffices?: ReadonlyArray<string>;
    // Unique client tags for filter dropdown
    readonly uniqueTags?: ReadonlyArray<string>;
    // Care coordinator filter
    readonly careCoordinatorSupportEnabled: boolean;
    readonly careCoordinators?: ReadonlyArray<CareCoordinatorRead>;
    readonly careCoordinatorFilter: Array<string> | null;
    readonly myCareCoordinator: CareCoordinatorRead | null;
    readonly onSelectCoordinator: (coordinatorId: string) => void;
    readonly onShowAllCoordinators: () => void;
    readonly clients?: ReadonlyArray<ShiftFilterClient>;
    readonly clientFilterIds: Array<string>;
    readonly onSelectClient: (clientId: string) => void;
    readonly onShowAllClients: () => void;
    readonly clientReferenceTerm: string;
};

function MultiShiftSelectionBar({
    selectedCount,
    maxCount,
    onClear,
    onCreateOutreach,
}: {
    readonly selectedCount: number;
    readonly maxCount: number;
    readonly onClear: () => void;
    readonly onCreateOutreach: () => void;
}) {
    const isVisible = selectedCount > 0;
    const isAtLimit = selectedCount >= maxCount;

    return (
        <div
            className={cn(
                'pointer-events-none sticky inset-x-0 bottom-4 z-20 flex justify-center transition-all duration-200 ease-out',
                isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-2 opacity-0',
            )}
            aria-hidden={!isVisible}
        >
            <div className="border-border bg-background text-foreground pointer-events-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center gap-2 rounded-full border px-3 py-1.5 shadow-md">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium">
                        {selectedCount} shift{selectedCount !== 1 ? 's' : ''}{' '}
                        selected
                    </span>
                    {isAtLimit && (
                        <span className="text-muted-foreground text-[11px]">
                            (max {maxCount})
                        </span>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClear}
                        className="text-foreground hover:bg-foreground/10 h-5 w-5"
                        aria-label="Clear selection"
                    >
                        <X className="h-3 w-3" />
                    </Button>
                </div>
                <div className="bg-foreground/20 h-4 w-px" />
                <Button
                    size="sm"
                    onClick={onCreateOutreach}
                    disabled={selectedCount === 0}
                    className="text-xs"
                >
                    Find Caregivers
                </Button>
            </div>
        </div>
    );
}

function ShiftMobileCard({
    shift,
    displayCaregiverNamesLastFirst,
}: {
    readonly shift: Shift;
    readonly displayCaregiverNamesLastFirst: boolean;
}) {
    const { timezone } = useOrganizationTimezone();
    const status = resolveShiftStatus(shift.status, shift.latest_stage);
    const indicatorColor = getShiftStageIndicatorColor(status);
    const caregiverName = shift.caregiver?.name
        ? formatCaregiverName(
              shift.caregiver.name,
              displayCaregiverNamesLastFirst,
          )
        : null;
    const startLabel = formatTime(shift.shift_start_time, timezone);
    const endLabel = formatTime(shift.shift_end_time, timezone);

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold">
                        {shift.patient_name || 'Unknown client'}
                    </div>
                    <div
                        className={cn(
                            'truncate text-sm',
                            caregiverName
                                ? 'text-foreground'
                                : 'text-muted-foreground italic',
                        )}
                    >
                        {caregiverName ?? 'Unassigned'}
                    </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5 text-xs">
                    <span
                        aria-hidden={true}
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: indicatorColor }}
                    />
                    <span>{formatShiftStatusText(status)}</span>
                </div>
            </div>
            <div className="text-muted-foreground flex items-center justify-between text-xs">
                <span>
                    {startLabel} – {endLabel}
                </span>
                {shift.patient_office_name ? (
                    <span className="truncate">
                        {shift.patient_office_name}
                    </span>
                ) : null}
            </div>
        </div>
    );
}

type ShiftListTableSectionProps = {
    readonly isLoading: boolean;
    readonly error: unknown;
    readonly displayMode: DisplayMode;
    readonly shifts: Array<Shift>;
    readonly columns: Array<ColumnDef<Shift>>;
    readonly getRowNavigation: (shift: Shift) => RowNavigation;
    readonly columnVisibilityKey: string;
    readonly initialColumnVisibility?: Record<string, boolean>;
    readonly columnFilters: ColumnFiltersState;
    readonly filterKeyPrefix: string;
    readonly toolbarProps: ShiftListToolbarBaseProps;
    readonly totalShifts: number;
    readonly currentPage: number;
    readonly pageSize: number;
    readonly onPageChange: (page: number) => void;
    readonly onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
    readonly displayCaregiverNamesLastFirst: boolean;
};

export function ShiftListTableSection({
    isLoading,
    error,
    displayMode,
    shifts,
    columns,
    getRowNavigation,
    columnVisibilityKey,
    initialColumnVisibility,
    columnFilters,
    filterKeyPrefix,
    toolbarProps,
    totalShifts,
    currentPage,
    pageSize,
    onPageChange,
    onColumnFiltersChange,
    displayCaregiverNamesLastFirst,
}: ShiftListTableSectionProps) {
    const { shiftsByStage, stageKeys } = useMemo(() => {
        const map = new Map<UnifiedShiftStatus, Array<Shift>>();
        shifts.forEach((shift) => {
            const key: UnifiedShiftStatus = resolveShiftStatus(
                shift.status,
                shift.latest_stage,
            );

            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push(shift);
        });
        return { shiftsByStage: map, stageKeys: [...map.keys()] };
    }, [shifts]);

    if (error) {
        return (
            <div className="flex h-32 items-center justify-center">
                <div className="text-sm text-red-500">Error loading shifts</div>
            </div>
        );
    }

    const totalPages = Math.ceil(totalShifts / pageSize);
    const showPagination = totalPages > 1;
    const startItem = currentPage * pageSize + 1;
    const endItem = Math.min((currentPage + 1) * pageSize, totalShifts);

    if (displayMode === 'ungrouped' || shifts.length === 0) {
        return (
            <div className="flex h-full min-h-0 flex-col gap-2 md:gap-4">
                <DataTable
                    className="flex min-h-0 flex-1 flex-col"
                    columns={columns}
                    data={shifts}
                    isLoading={isLoading}
                    getRowNavigation={getRowNavigation}
                    getRowClassName={(shift) =>
                        shift.is_confirmation_skipped
                            ? 'opacity-60 text-muted-foreground'
                            : ''
                    }
                    pageSize={pageSize}
                    hidePagination={true}
                    initialSorting={[
                        {
                            id: 'shift_start_time',
                            desc: false,
                        },
                    ]}
                    columnVisibilityKey={columnVisibilityKey}
                    initialColumnVisibility={initialColumnVisibility}
                    columnFilters={columnFilters}
                    filterKeyPrefix={filterKeyPrefix}
                    toolbarClassName="max-md:p-1.5"
                    onColumnFiltersChange={onColumnFiltersChange}
                    mobileRowsClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]"
                    tableRowsClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain"
                    renderToolbar={(table) => (
                        <ShiftListToolbar
                            {...toolbarProps}
                            displayMode="ungrouped"
                            table={table}
                        />
                    )}
                    renderMobileCard={(shift) => (
                        <ShiftMobileCard
                            shift={shift}
                            displayCaregiverNamesLastFirst={
                                displayCaregiverNamesLastFirst
                            }
                        />
                    )}
                />
                {showPagination && (
                    <div className="flex shrink-0 flex-col items-center gap-2 border-t pt-2 md:flex-row md:justify-between md:pt-4">
                        <div className="text-muted-foreground text-sm">
                            Showing {startItem} - {endItem} of {totalShifts}{' '}
                            shifts
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(currentPage - 1)}
                                disabled={currentPage === 0}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                <span className="hidden md:inline">
                                    Previous
                                </span>
                            </Button>
                            <span className="text-sm">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages - 1}
                            >
                                <span className="hidden md:inline">Next</span>
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-2 md:gap-4">
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] md:space-y-8">
                {stageKeys.map((status) => {
                    const statusShifts = shiftsByStage.get(status) ?? [];
                    const statusLabel = formatShiftStatusText(status);
                    const indicatorColor = getShiftStageIndicatorColor(status);

                    return (
                        <div key={status} className="space-y-2 md:space-y-3">
                            <h3 className="flex items-center gap-2 text-base font-semibold md:text-lg">
                                {indicatorColor && (
                                    <span
                                        className="h-3 w-3 rounded-full"
                                        style={{
                                            backgroundColor: indicatorColor,
                                        }}
                                    />
                                )}
                                {statusLabel} ({statusShifts.length})
                            </h3>
                            <DataTable
                                className="flex min-h-0 flex-col max-md:min-h-[60svh]"
                                columns={columns}
                                data={statusShifts}
                                isLoading={isLoading}
                                getRowNavigation={getRowNavigation}
                                getRowClassName={(shift) =>
                                    shift.is_confirmation_skipped
                                        ? 'opacity-60 text-muted-foreground'
                                        : ''
                                }
                                pageSize={pageSize}
                                hidePagination={true}
                                initialSorting={[
                                    {
                                        id: 'shift_start_time',
                                        desc: false,
                                    },
                                ]}
                                columnVisibilityKey={columnVisibilityKey}
                                initialColumnVisibility={
                                    initialColumnVisibility
                                }
                                columnFilters={columnFilters}
                                filterKeyPrefix={filterKeyPrefix}
                                toolbarClassName="max-md:p-1.5"
                                onColumnFiltersChange={onColumnFiltersChange}
                                mobileRowsClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] md:max-h-[24rem]"
                                tableRowsClassName="max-h-[24rem] overflow-y-auto overscroll-contain"
                                renderToolbar={(table) => (
                                    <ShiftListToolbar
                                        {...toolbarProps}
                                        displayMode="grouped"
                                        table={table}
                                    />
                                )}
                                renderMobileCard={(shift) => (
                                    <ShiftMobileCard
                                        shift={shift}
                                        displayCaregiverNamesLastFirst={
                                            displayCaregiverNamesLastFirst
                                        }
                                    />
                                )}
                            />
                        </div>
                    );
                })}
            </div>
            {showPagination && (
                <div className="flex shrink-0 flex-col items-center gap-2 border-t pt-2 md:flex-row md:justify-between md:pt-4">
                    <div className="text-muted-foreground text-sm">
                        Showing {startItem} - {endItem} of {totalShifts} shifts
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 0}
                        >
                            <ChevronLeft className="h-4 w-4" />
                            <span className="hidden md:inline">Previous</span>
                        </Button>
                        <span className="text-sm">
                            Page {currentPage + 1} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages - 1}
                        >
                            <span className="hidden md:inline">Next</span>
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

type ShiftListToolbarProps = ShiftListToolbarBaseProps & {
    readonly table: Table<Shift>;
    readonly displayMode: DisplayMode;
};

function ShiftListToolbar({
    table,
    shifts,
    allAssignedShiftIds,
    displayMode,
    isLoading,
    onSyncShifts,
    isSyncing,
    hasSyncEnabledAccount,
    pendingMessagesCount,
    isLoadingPendingMessagesCount,
    onShowPendingMessages,
    mode,
    clockInOutEnabled,
    createShiftEnabled,
    onShowSandboxSetupModal,
    onShowCreateTestShiftModal,
    onRequestSendConfirmations,
    isSendingConfirmations,
    isSelectedDateInPast,
    shiftStartIso,
    shiftEndIso,
    searchQuery,
    onSearchChange,
    serverSideGlobalFilter,
    uniqueOffices,
    uniqueTags,
    careCoordinatorSupportEnabled,
    careCoordinators,
    careCoordinatorFilter,
    myCareCoordinator,
    onSelectCoordinator,
    onShowAllCoordinators,
    clients,
    clientFilterIds,
    onSelectClient,
    onShowAllClients,
    clientReferenceTerm,
}: ShiftListToolbarProps) {
    const visibleRows = table.getRowModel().rows;
    const filteredAssignedIds = visibleRows
        .map((row) => row.original as Shift)
        .filter(
            (shift) =>
                shift.assigned_caregiver_id && !shift.is_confirmation_skipped,
        )
        .map((shift) => shift.id);

    const unconfirmedIds = filteredAssignedIds.filter((id) => {
        const shift = shifts.find((item) => item.id === id);
        return (
            shift?.latest_stage === null || shift?.latest_stage === 'assigned'
        );
    });

    const tableState = table.state;
    const hasColumnFilters = tableState.columnFilters.length > 0;

    const availableStatusValues = useMemo(
        () => statusFilterOptions({ clockInOutEnabled }),
        [clockInOutEnabled],
    );
    const hasGlobal =
        !!tableState.globalFilter &&
        String(tableState.globalFilter).trim() !== '';
    const isFilterActive =
        hasColumnFilters ||
        hasGlobal ||
        filteredAssignedIds.length !== allAssignedShiftIds.length;

    const eligibleShiftCount = isFilterActive
        ? filteredAssignedIds.length
        : allAssignedShiftIds.length;

    const showBulkActions = eligibleShiftCount > 0;

    const hasPendingMessages =
        !isLoadingPendingMessagesCount && pendingMessagesCount > 0;

    const sendButtonDisabled =
        eligibleShiftCount === 0 ||
        isSendingConfirmations ||
        isSelectedDateInPast;

    const handleSendAllConfirmations = () => {
        if (displayMode === 'ungrouped') {
            const ids = isFilterActive
                ? unconfirmedIds
                : allAssignedShiftIds.filter((id) => {
                      const shift = shifts.find((s) => s.id === id);
                      return (
                          shift?.latest_stage === null ||
                          shift?.latest_stage === 'assigned'
                      );
                  });

            if (ids.length === 0) {
                toast.error('No unconfirmed shifts to send confirmations to.', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                return;
            }

            onRequestSendConfirmations({ mode: 'ids', ids });
            return;
        }

        if (isFilterActive) {
            onRequestSendConfirmations({
                mode: 'ids',
                ids: filteredAssignedIds,
            });
            return;
        }

        if (shiftStartIso && shiftEndIso) {
            onRequestSendConfirmations({
                mode: 'date_range',
                startIso: shiftStartIso,
                endIso: shiftEndIso,
            });
            return;
        }

        console.error('No filters and no selected date');
    };

    const sendButtonLabel = isSendingConfirmations
        ? 'Sending...'
        : `Send All Confirmations${
              isFilterActive && unconfirmedIds.length > 0
                  ? ` (${unconfirmedIds.length})`
                  : ''
          }`;

    const [coordinatorSearchValue, setCoordinatorSearchValue] = useState('');
    // Snapshot of selected IDs captured when dropdown opens - used for stable sorting
    const [selectionSnapshotIds, setSelectionSnapshotIds] = useState<
        Set<string>
    >(new Set());

    // Capture selection snapshot when dropdown opens (for stable sort order)
    const captureSelectionSnapshot = useCallback(() => {
        const currentSelection =
            careCoordinatorFilter ??
            (myCareCoordinator ? [myCareCoordinator.id] : []);
        setSelectionSnapshotIds(new Set(currentSelection));
    }, [careCoordinatorFilter, myCareCoordinator]);

    const careCoordinatorSelectedCount =
        careCoordinatorFilter === null
            ? myCareCoordinator
                ? 1
                : 0
            : careCoordinatorFilter.length;

    const careCoordinatorActiveLabels = useMemo(() => {
        if (careCoordinatorFilter === null && myCareCoordinator) {
            return [formatCoordinatorName(myCareCoordinator)];
        }
        if (
            !careCoordinators ||
            careCoordinatorFilter === null ||
            careCoordinatorFilter.length === 0
        ) {
            return [];
        }
        return careCoordinatorFilter
            .map((id) => {
                if (id === UNASSIGNED_CARE_COORDINATOR_ID) {
                    return 'No Care Coordinator';
                }
                const cc = careCoordinators.find((c) => c.id === id);
                return cc ? formatCoordinatorName(cc) : null;
            })
            .filter((name): name is string => name !== null);
    }, [careCoordinators, careCoordinatorFilter, myCareCoordinator]);

    const filteredCoordinators = useMemo(() => {
        if (!careCoordinators) {
            return [];
        }

        // Use snapshot for sorting (captured when combobox opened) for stable order
        const wasSelectedAtOpen = (cc: CareCoordinatorRead) =>
            selectionSnapshotIds.has(cc.id);

        let filtered = [...careCoordinators];
        if (coordinatorSearchValue.trim()) {
            const searchLower = coordinatorSearchValue.toLowerCase();
            filtered = filtered.filter((cc) => {
                const name = formatCoordinatorName(cc).toLowerCase();
                const email = (cc.email ?? '').toLowerCase();
                return (
                    name.includes(searchLower) || email.includes(searchLower)
                );
            });
        }

        return filtered.sort((a, b) => {
            const aSelected = wasSelectedAtOpen(a);
            const bSelected = wasSelectedAtOpen(b);
            if (aSelected && !bSelected) {
                return -1;
            }
            if (!aSelected && bSelected) {
                return 1;
            }
            return formatCoordinatorName(a).localeCompare(
                formatCoordinatorName(b),
            );
        });
    }, [careCoordinators, coordinatorSearchValue, selectionSnapshotIds]);

    const clientFilter = useShiftClientFilterConfig({
        clients,
        selectedIds: clientFilterIds,
        clientReferenceTerm,
        onToggle: onSelectClient,
        onClear: onShowAllClients,
    });
    const customFilters: Array<CustomFilterConfig> = clientFilter
        ? [clientFilter]
        : [];

    if (
        careCoordinatorSupportEnabled &&
        careCoordinators &&
        careCoordinators.length > 0
    ) {
        customFilters.push({
            id: 'care_coordinator',
            title: 'Care Coordinator',
            icon: Users,
            selectedCount: careCoordinatorSelectedCount,
            activeFilterLabels: careCoordinatorActiveLabels,
            onClear: onShowAllCoordinators,
            onOpen: captureSelectionSnapshot,
            renderValueSelection: ({ open, onOpenChange }) => {
                const valueLabel = careCoordinatorActiveLabels.join(', ');
                return (
                    <Combobox
                        open={open}
                        onOpenChange={(newOpen) => {
                            if (newOpen) {
                                captureSelectionSnapshot();
                            }
                            onOpenChange(newOpen);
                        }}
                        displayValue={valueLabel}
                        placeholder=""
                        searchValue={coordinatorSearchValue}
                        onSearchValueChange={setCoordinatorSearchValue}
                        searchPlaceholder="Search coordinators..."
                        triggerClassName="h-7 max-w-[16rem] min-w-[6rem] rounded-full px-3 text-xs"
                        contentClassName="w-72"
                        matchTriggerWidth={false}
                        fullWidth={false}
                        commandProps={{
                            filter: () => 1,
                        }}
                    >
                        <CommandEmpty>No coordinators found.</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                key={UNASSIGNED_CARE_COORDINATOR_ID}
                                value="No Care Coordinator unassigned"
                                onSelect={() =>
                                    onSelectCoordinator(
                                        UNASSIGNED_CARE_COORDINATOR_ID,
                                    )
                                }
                            >
                                <div className="flex flex-1 flex-col">
                                    <span className="text-muted-foreground italic">
                                        No Care Coordinator
                                    </span>
                                </div>
                                {careCoordinatorFilter?.includes(
                                    UNASSIGNED_CARE_COORDINATOR_ID,
                                ) && <Check className="h-4 w-4 shrink-0" />}
                            </CommandItem>
                            {filteredCoordinators.map((coordinator) => {
                                const isSelected =
                                    careCoordinatorFilter?.includes(
                                        coordinator.id,
                                    ) ??
                                    (careCoordinatorFilter === null &&
                                        myCareCoordinator?.id ===
                                            coordinator.id);
                                return (
                                    <CommandItem
                                        key={coordinator.id}
                                        value={`${formatCoordinatorName(coordinator)} ${coordinator.email ?? ''}`}
                                        onSelect={() =>
                                            onSelectCoordinator(coordinator.id)
                                        }
                                    >
                                        <div className="flex flex-1 flex-col">
                                            <span>
                                                {formatCoordinatorName(
                                                    coordinator,
                                                )}
                                            </span>
                                            {coordinator.email && (
                                                <span className="text-muted-foreground text-xs">
                                                    {coordinator.email}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <Check className="h-4 w-4 shrink-0" />
                                        )}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </Combobox>
                );
            },
            children: (
                <Command filter={() => 1} className="max-h-72 overflow-hidden">
                    <CommandInput
                        placeholder="Search coordinators..."
                        value={coordinatorSearchValue}
                        onValueChange={setCoordinatorSearchValue}
                    />
                    <CommandList className="max-h-56">
                        <CommandEmpty>No coordinators found.</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                key={UNASSIGNED_CARE_COORDINATOR_ID}
                                value="No Care Coordinator unassigned"
                                onSelect={() =>
                                    onSelectCoordinator(
                                        UNASSIGNED_CARE_COORDINATOR_ID,
                                    )
                                }
                            >
                                <div className="flex flex-1 flex-col">
                                    <span className="text-muted-foreground italic">
                                        No Care Coordinator
                                    </span>
                                </div>
                                {careCoordinatorFilter?.includes(
                                    UNASSIGNED_CARE_COORDINATOR_ID,
                                ) && <Check className="h-4 w-4 shrink-0" />}
                            </CommandItem>
                            {filteredCoordinators.map((coordinator) => {
                                const isSelected =
                                    careCoordinatorFilter?.includes(
                                        coordinator.id,
                                    ) ??
                                    (careCoordinatorFilter === null &&
                                        myCareCoordinator?.id ===
                                            coordinator.id);
                                return (
                                    <CommandItem
                                        key={coordinator.id}
                                        value={`${formatCoordinatorName(coordinator)} ${coordinator.email ?? ''}`}
                                        onSelect={() =>
                                            onSelectCoordinator(coordinator.id)
                                        }
                                    >
                                        <div className="flex flex-1 flex-col">
                                            <span>
                                                {formatCoordinatorName(
                                                    coordinator,
                                                )}
                                            </span>
                                            {coordinator.email && (
                                                <span className="text-muted-foreground text-xs">
                                                    {coordinator.email}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <Check className="h-4 w-4 shrink-0" />
                                        )}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            ),
        });
    }

    return (
        <div className="flex flex-col gap-1.5 md:flex-row md:flex-wrap md:items-start md:justify-between md:gap-2">
            <div className="min-w-0 flex-1">
                <DataTableToolbar
                    table={table}
                    globalFilterPlaceholder={`Search caregiver or ${clientReferenceTerm}…`}
                    globalFilterValue={searchQuery}
                    onGlobalFilterChange={onSearchChange}
                    serverSideGlobalFilter={serverSideGlobalFilter}
                    customFilters={customFilters}
                    facetedFilters={[
                        {
                            columnId: 'patient_office_name',
                            title: 'Office',
                            icon: Building,
                            valueFormatter: (value) =>
                                formatOfficeName(String(value || '—')),
                            limit: 50,
                            predefinedOptions: uniqueOffices,
                        },
                        ...(uniqueTags?.length ||
                        table.getColumn('patient_tags')?.getFilterValue()
                            ? [
                                  {
                                      columnId: 'patient_tags',
                                      title: 'Tags',
                                      icon: Tag,
                                      predefinedOptions: uniqueTags,
                                  },
                              ]
                            : []),
                        {
                            columnId: 'unified_status',
                            title: 'Status',
                            icon: CircleDot,
                            valueFormatter: (value) =>
                                (value as string)
                                    .replace(/_/g, ' ')
                                    .replace(/\b\w/g, (l) => l.toUpperCase()),
                            predefinedOptions: availableStatusValues,
                        },
                    ]}
                    isLoading={isLoading}
                    mobileCompact={true}
                />
            </div>
            <div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto pb-0.5 md:flex-wrap md:gap-2 md:overflow-visible md:pb-0">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onSyncShifts}
                    disabled={isSyncing || !hasSyncEnabledAccount}
                    className="cursor-pointer max-md:h-9 max-md:w-9 max-md:px-0"
                    aria-label={isSyncing ? 'Syncing shifts' : 'Sync shifts'}
                >
                    {isSyncing ? (
                        <React.Fragment>
                            <Loader2 className="h-4 w-4 animate-spin md:mr-2" />
                            <span className="hidden md:inline">Syncing…</span>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <RefreshCw className="h-4 w-4 md:mr-2" />
                            <span className="hidden md:inline">Sync</span>
                        </React.Fragment>
                    )}
                </Button>
                {hasPendingMessages && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="cursor-pointer max-md:h-9 max-md:w-9 max-md:px-0"
                        onClick={onShowPendingMessages}
                        aria-label={`Approve responses (${pendingMessagesCount})`}
                    >
                        <MessageSquareText className="h-4 w-4 md:mr-2" />
                        <span className="hidden md:inline">
                            Approve Responses ({pendingMessagesCount})
                        </span>
                    </Button>
                )}
                {(mode === 'sandbox' || createShiftEnabled) && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="cursor-pointer max-md:h-9 max-md:w-9 max-md:px-0"
                        onClick={onShowCreateTestShiftModal}
                        aria-label="Create shift"
                    >
                        {mode === 'sandbox' ? (
                            <TestTube2 className="h-4 w-4" />
                        ) : (
                            <Plus className="h-4 w-4" />
                        )}
                        <span className="hidden md:inline">Create Shift</span>
                    </Button>
                )}
                {mode === 'sandbox' && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onShowSandboxSetupModal}
                        className="cursor-pointer max-md:h-9 max-md:w-9 max-md:px-0"
                        aria-label="Generate test data"
                    >
                        <TestTube2 className="h-4 w-4 md:mr-2" />
                        <span className="hidden md:inline">
                            Generate Test Data
                        </span>
                    </Button>
                )}
                {showBulkActions && (
                    <Button
                        variant="default"
                        size="sm"
                        className="cursor-pointer max-md:h-9 max-md:w-9 max-md:px-0"
                        onClick={handleSendAllConfirmations}
                        disabled={sendButtonDisabled}
                        aria-label={sendButtonLabel}
                    >
                        <Send className="h-4 w-4 md:hidden" />
                        <span className="hidden md:inline">
                            {sendButtonLabel}
                        </span>
                    </Button>
                )}
            </div>
        </div>
    );
}

function ClientSection({
    organizationId,
    shiftId,
    currentClientId,
    onClientChange,
    onClientUpdated,
    clientReferenceTerm,
    clientReferenceTermTitle,
}: {
    organizationId: string;
    shiftId: string;
    currentClientId: string | null;
    onClientChange: (newClientId: string | null) => void;
    onClientUpdated: () => void;
    clientReferenceTerm: string;
    clientReferenceTermTitle: string;
}) {
    const [isUpdating, setIsUpdating] = useState(false);

    const { data: clients, isLoading } = useOrganizationClients(
        organizationId,
        {
            reason: 'shift-detail-modal-client-section',
        },
    );

    const currentClient = useMemo(() => {
        if (!currentClientId || !clients) {
            return null;
        }
        return clients.find((c) => c.id === currentClientId) ?? null;
    }, [currentClientId, clients]);

    const handleClientChange = async (newClientId: string) => {
        const actualNewClientId =
            newClientId === '__none__' ? null : newClientId;
        if (actualNewClientId === currentClientId) {
            return;
        }

        // Optimistically update via parent state
        onClientChange(actualNewClientId);
        setIsUpdating(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/shifts/{shift_id}',
                pathParams: {
                    organization_id: organizationId,
                    shift_id: shiftId,
                },
                body: {
                    client_id: actualNewClientId,
                },
            });
            onClientUpdated();
            toast.success('Client updated', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
        } catch (error) {
            console.error('Error updating client:', error);
            toast.error('Failed to update client', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            // Revert on error via parent state
            onClientChange(currentClientId);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div>
            <p className="text-muted-foreground text-sm font-medium">
                {clientReferenceTermTitle}
            </p>
            {isLoading ? (
                <Skeleton className="h-8 w-48" />
            ) : (
                <Select
                    value={currentClientId ?? '__none__'}
                    onValueChange={handleClientChange}
                    disabled={isUpdating}
                >
                    <SelectTrigger className="h-8 w-48">
                        <SelectValue
                            placeholder={
                                isUpdating
                                    ? 'Updating...'
                                    : `Select ${clientReferenceTerm}...`
                            }
                        >
                            {currentClient
                                ? `${currentClient.last_name}, ${currentClient.first_name}`
                                : currentClientId
                                  ? `Unknown ${clientReferenceTerm}`
                                  : `No ${clientReferenceTerm}`}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">{`No ${clientReferenceTerm}`}</SelectItem>
                        {clients?.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                                {client.last_name}, {client.first_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}

function CareCoordinatorSection({
    organizationId,
    clientId,
    allCareCoordinators,
    clientReferenceTerm,
}: {
    organizationId: string;
    clientId: string | null;
    allCareCoordinators: ReadonlyArray<CareCoordinatorRead> | undefined;
    clientReferenceTerm: string;
}) {
    const [open, setOpen] = useState(false);

    const {
        data: assignedCoordinators,
        isLoading,
        mutate,
    } = useClientCareCoordinators(organizationId, clientId, {
        reason: 'shift-detail-modal',
    });

    const assignedIds = useMemo(() => {
        return new Set(assignedCoordinators?.map((c) => c.id) ?? []);
    }, [assignedCoordinators]);

    const handleToggle = async (careCoordinatorId: string) => {
        if (!clientId) {
            return;
        }

        const isAssigned = assignedIds.has(careCoordinatorId);

        // Optimistically update the UI - wrap in API response structure
        let needsRevalidation = false;
        if (isAssigned) {
            mutate(
                {
                    care_coordinators:
                        assignedCoordinators?.filter(
                            (c) => c.id !== careCoordinatorId,
                        ) ?? [],
                },
                { revalidate: false },
            );
        } else {
            const coordToAdd = allCareCoordinators?.find(
                (c) => c.id === careCoordinatorId,
            );
            if (coordToAdd) {
                mutate(
                    {
                        care_coordinators: [
                            ...(assignedCoordinators ?? []),
                            coordToAdd,
                        ],
                    },
                    { revalidate: false },
                );
            } else {
                // Coordinator not in list, will need to revalidate after API call
                needsRevalidation = true;
            }
        }

        try {
            if (isAssigned) {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/clients/{client_id}/care-coordinators/{care_coordinator_id}',
                    pathParams: {
                        organization_id: organizationId,
                        client_id: clientId,
                        care_coordinator_id: careCoordinatorId,
                    },
                });
                toast.success('Care coordinator unassigned', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            } else {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/clients/{client_id}/care-coordinators',
                    pathParams: {
                        organization_id: organizationId,
                        client_id: clientId,
                    },
                    body: { care_coordinator_id: careCoordinatorId },
                });
                toast.success('Care coordinator assigned', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
                if (needsRevalidation) {
                    mutate();
                }
            }
        } catch (error) {
            // On error, revalidate from server to get correct state
            // This avoids issues with stale rollback state from concurrent operations
            mutate();
            console.error('Error updating care coordinator:', error);
            toast.error('Failed to update care coordinator', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
        }
    };

    // Get the display value for the dropdown
    const displayValue = useMemo(() => {
        if (!assignedCoordinators || assignedCoordinators.length === 0) {
            return null;
        }
        return assignedCoordinators
            .map((c) => `${c.first_name} ${c.last_name}`)
            .join(', ');
    }, [assignedCoordinators]);

    if (!clientId) {
        return (
            <div>
                <p className="text-muted-foreground text-sm font-medium">
                    Care Coordinator
                </p>
                <p className="text-muted-foreground text-sm italic">
                    No {clientReferenceTerm} linked to this shift
                </p>
            </div>
        );
    }

    return (
        <div>
            <p className="text-muted-foreground text-sm font-medium">
                Care Coordinator
            </p>
            {isLoading ? (
                <Skeleton className="h-8 w-48" />
            ) : (
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild={true}>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="h-8 w-48 justify-between font-normal"
                        >
                            <span className="truncate">
                                {displayValue ?? 'No coordinator'}
                            </span>
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-0" align="start">
                        <Command>
                            <CommandGroup>
                                {allCareCoordinators?.map((coord) => (
                                    <CommandItem
                                        key={coord.id}
                                        value={coord.id}
                                        onSelect={() => handleToggle(coord.id)}
                                    >
                                        <span className="text-muted-foreground flex h-4 w-4 items-center justify-center">
                                            {assignedIds.has(coord.id) && (
                                                <Check className="h-4 w-4" />
                                            )}
                                        </span>
                                        {coord.first_name} {coord.last_name}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </Command>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
}

type ShiftDetailsDialogProps = {
    readonly selectedShift: Shift | null;
    readonly selectedShiftDetails: Shift | null;
    readonly timezone: string;
    readonly displayCaregiverNamesLastFirst: boolean;
    readonly caregiverUnassignmentEnabled: boolean;
    readonly caregivers: CaregiversData;
    readonly caregiverForm: UseFormReturn<{ caregiver: string }>;
    readonly mode: Mode;
    readonly organizationId: string | undefined | null;
    readonly hasActiveCallout: boolean;
    readonly activeCallout: Outreach | null;
    readonly activeClockReminder: Outreach | null;
    readonly hasWellSkyAccount: boolean;
    readonly hasSyncEnabledAccountForShift: boolean;
    readonly dataSourceAccounts?: ReadonlyArray<OrganizationDataSourceAccount>;
    readonly onSyncSelectedShift: () => void;
    readonly isSyncingSelectedShift: boolean;
    readonly onCreateCallout: (shift: Shift) => void;
    readonly onSetCaregiver: (
        shift: Shift,
        caregiverId: string,
        ehrName?: string,
    ) => Promise<void> | void;
    readonly onClose: () => void;
    readonly onNavigateToOutreach: (outreachId: string) => void;
    readonly userIsAdmin: boolean;
    readonly allCareCoordinators?: ReadonlyArray<CareCoordinatorRead>;
    readonly onShiftUpdated?: () => void;
    readonly readOnly?: boolean;
    readonly openedFromCaregiverModal?: boolean;
    readonly clientReferenceTerm: string;
    readonly clientReferenceTermTitle: string;
};

function ShiftDetailsDialog({
    selectedShift,
    selectedShiftDetails,
    timezone,
    displayCaregiverNamesLastFirst,
    caregiverUnassignmentEnabled,
    caregivers,
    caregiverForm,
    mode,
    organizationId,
    hasActiveCallout,
    activeCallout,
    activeClockReminder,
    hasWellSkyAccount,
    hasSyncEnabledAccountForShift,
    dataSourceAccounts,
    onSyncSelectedShift,
    isSyncingSelectedShift,
    onCreateCallout,
    onSetCaregiver,
    onClose,
    onNavigateToOutreach,
    userIsAdmin,
    allCareCoordinators,
    onShiftUpdated,
    readOnly = false,
    openedFromCaregiverModal = false,
    clientReferenceTerm,
    clientReferenceTermTitle,
}: ShiftDetailsDialogProps) {
    // Local state for optimistic client ID updates shared between ClientSection and CareCoordinatorSection
    const [localClientId, setLocalClientId] = useState<string | null>(
        selectedShift?.client_id ?? null,
    );
    const isMobile = useIsMobile();
    const isDevelopment = getRunEnvironment() === 'development';
    const { data: clients } = useOrganizationClients(organizationId ?? null, {
        reason: 'shift-details-client-link',
    });
    // Sync local state when the selected shift changes
    useEffect(() => {
        setLocalClientId(selectedShift?.client_id ?? null);
    }, [selectedShift?.client_id]);

    const handleClientChange = (newClientId: string | null) => {
        setLocalClientId(newClientId);
    };

    const clientById = useMemo(() => {
        const map = new Map<string, OrgClientRead>();
        for (const client of clients ?? []) {
            map.set(client.id, client);
        }
        return map;
    }, [clients]);

    const clientExternalLink = useMemo(() => {
        if (!localClientId) {
            return null;
        }
        const client = clientById.get(localClientId);
        if (!client?.external_id || !client?.source_system) {
            return null;
        }
        const dataSourceAccount = dataSourceAccounts?.find(
            (account) => account.type === client.source_system,
        );
        if (!dataSourceAccount) {
            return null;
        }
        const externalUrl = generateExternalClientUrl(
            client.source_system,
            client.external_id,
            dataSourceAccount.base_url,
        );
        const systemName = getDataSourceDisplayName(client.source_system);
        return {
            externalUrl,
            sourceSystem: client.source_system,
            systemName,
        };
    }, [clientById, dataSourceAccounts, localClientId]);

    const shiftUnassignmentEhrName =
        selectedShift?.external_id &&
        clientExternalLink &&
        SHIFT_UNASSIGNMENT_WRITEBACK_SOURCES.has(
            clientExternalLink.sourceSystem,
        )
            ? clientExternalLink.systemName
            : undefined;

    const showClientLinkPreview =
        isDevelopment && !!localClientId && !clientExternalLink;
    const clientPhoneNumber = useMemo(() => {
        if (!localClientId) {
            return null;
        }
        return clientById.get(localClientId)?.phone_number ?? null;
    }, [clientById, localClientId]);

    const isOpen = Boolean(selectedShift);
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const headerButtons = selectedShift ? (
        <ShiftDetailsHeaderActions
            shift={selectedShift}
            organizationId={organizationId}
            readOnly={readOnly}
            hasActiveCallout={hasActiveCallout}
            activeCallout={activeCallout}
            activeClockReminder={activeClockReminder}
            hasSyncEnabledAccountForShift={hasSyncEnabledAccountForShift}
            isSyncingSelectedShift={isSyncingSelectedShift}
            onSyncSelectedShift={onSyncSelectedShift}
            onCreateCallout={onCreateCallout}
            onNavigateToOutreach={onNavigateToOutreach}
        />
    ) : null;

    const detailsGrid = selectedShift ? (
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto md:grid md:grid-cols-5 md:gap-6 md:overflow-hidden">
            <div className="space-y-2 overflow-y-auto md:col-span-2 md:pr-4">
                <h3 className="text-lg font-semibold">Shift Information</h3>
                <div className="grid grid-cols-1 gap-2">
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Time
                        </p>
                        <p className="text-sm">
                            {formatTime(
                                selectedShift.shift_start_time,
                                timezone,
                            )}{' '}
                            -{' '}
                            {formatTime(selectedShift.shift_end_time, timezone)}
                        </p>
                        {openedFromCaregiverModal && (
                            <p className="text-muted-foreground text-xs">
                                {formatFullDate(
                                    selectedShift.shift_start_time,
                                    timezone,
                                )}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Status
                        </p>
                        {(() => {
                            const internalStatus =
                                selectedShift.status as ShiftStatus;

                            const status = resolveShiftStatus(
                                internalStatus,
                                selectedShift.latest_stage,
                            );

                            const hasError = !!selectedShift.latest_stage_error;
                            const statusStyle = getShiftTagStyle(status);
                            const sentAt =
                                selectedShift.latest_stage_created_at;
                            const doneAtSection = sentAt &&
                                isStatusToShowSentAt(status, selectedShift) && (
                                    <div className="text-muted-foreground mt-1 text-xs">
                                        Sent: {formatTime(sentAt, timezone)}
                                    </div>
                                );
                            const badgeElement = (
                                <Badge
                                    className={cn(
                                        'rounded px-2 py-0.5 text-xs font-medium',
                                        statusStyle.className,
                                    )}
                                    style={statusStyle.style}
                                >
                                    <span className="flex items-center gap-1">
                                        {hasError && (
                                            <AlertCircle className="h-3 w-3" />
                                        )}
                                        {formatShiftStatusText(status)}
                                    </span>
                                </Badge>
                            );

                            return (
                                <div className="flex items-center gap-2">
                                    <div>
                                        {hasError ? (
                                            <TooltipProvider
                                                delayDuration={150}
                                            >
                                                <TooltipPrimitive.Root>
                                                    <TooltipTrigger
                                                        asChild={true}
                                                    >
                                                        {badgeElement}
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>
                                                            {
                                                                selectedShift.latest_stage_error
                                                            }
                                                        </p>
                                                    </TooltipContent>
                                                </TooltipPrimitive.Root>
                                            </TooltipProvider>
                                        ) : (
                                            badgeElement
                                        )}
                                        {!hasError && doneAtSection}
                                    </div>
                                    {selectedShift.is_confirmation_skipped && (
                                        <SkippedConfirmationTooltip />
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                    <div>
                        {(() => {
                            const externalStatus =
                                selectedShift.external_status;
                            const syncedAt =
                                selectedShift.external_snapshot_synced_at;

                            return (
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <p className="text-muted-foreground text-sm font-medium">
                                            {hasWellSkyAccount
                                                ? `${getDataSourceDisplayName('wellsky')} Status`
                                                : 'EHR Status'}
                                        </p>
                                        {syncedAt && (
                                            <span className="text-muted-foreground text-xs">
                                                (Last synced at{' '}
                                                {formatTime(syncedAt, timezone)}
                                                )
                                            </span>
                                        )}
                                    </div>
                                    {externalStatus ? (
                                        <span className="border-border text-muted-foreground w-fit rounded border px-1 text-[9px] uppercase">
                                            {formatExternalStatus(
                                                externalStatus,
                                            )}
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground text-sm">
                                            —
                                        </span>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            {clientReferenceTermTitle}
                        </p>
                        <div className="flex items-center gap-2">
                            <p className="text-sm">
                                {selectedShift.patient_name}
                            </p>
                            {clientExternalLink ? (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    asChild={true}
                                    className="h-7 w-7"
                                >
                                    <a
                                        href={clientExternalLink.externalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`View in ${clientExternalLink.systemName}`}
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        <span className="sr-only">
                                            View in{' '}
                                            {clientExternalLink.systemName}
                                        </span>
                                    </a>
                                </Button>
                            ) : showClientLinkPreview ? (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    disabled={true}
                                    title="View in EHR (preview)"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    <span className="sr-only">
                                        View in EHR (preview)
                                    </span>
                                </Button>
                            ) : null}
                        </div>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            {clientReferenceTermTitle} Phone
                        </p>
                        <p className="text-sm">{clientPhoneNumber || '—'}</p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Caregiver
                        </p>
                        <ShiftCaregiverControl
                            shift={selectedShift}
                            caregivers={caregivers}
                            caregiverForm={caregiverForm}
                            displayNamesLastFirst={
                                displayCaregiverNamesLastFirst
                            }
                            caregiverUnassignmentEnabled={
                                caregiverUnassignmentEnabled
                            }
                            ehrName={shiftUnassignmentEhrName}
                            mode={mode}
                            readOnly={readOnly}
                            onSetCaregiver={onSetCaregiver}
                        />
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Role
                        </p>
                        <p className="text-sm">
                            {selectedShift.role_required || '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Caregiver Phone
                        </p>
                        {(() => {
                            const contacts =
                                selectedShiftDetails?.caregiver?.contacts ||
                                selectedShift.caregiver?.contacts ||
                                [];
                            const phoneContacts = contacts.filter(
                                (c) => c.type === 'phone',
                            );

                            if (phoneContacts.length === 0) {
                                return <p className="text-sm">—</p>;
                            }

                            if (phoneContacts.length === 1) {
                                const phone = phoneContacts[0]!;
                                return (
                                    <p className="text-sm">{phone.address}</p>
                                );
                            }

                            return (
                                <ul className="list-disc pl-4 text-sm">
                                    {phoneContacts.map((phone, idx) => (
                                        <li
                                            key={idx}
                                            className={
                                                phone.is_primary
                                                    ? 'font-semibold'
                                                    : ''
                                            }
                                        >
                                            {phone.address}
                                        </li>
                                    ))}
                                </ul>
                            );
                        })()}
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Address
                        </p>
                        <p className="text-sm">
                            {selectedShift.patient_address || '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            City
                        </p>
                        <p className="text-sm">
                            {selectedShift.patient_city || '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            State
                        </p>
                        <p className="text-sm">
                            {selectedShift.patient_state || '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Office
                        </p>
                        <p className="text-sm">
                            {formatOfficeName(
                                selectedShift.patient_office_name,
                            )}
                        </p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Last Updated
                        </p>
                        <p className="text-sm">
                            {formatTime(selectedShift.updated_at, timezone)}
                        </p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-sm font-medium">
                            Last Synced
                        </p>
                        <p className="text-sm">
                            {selectedShift.external_snapshot_synced_at
                                ? `${formatFullDate(
                                      selectedShift.external_snapshot_synced_at,
                                      timezone,
                                  )} ${formatTime(selectedShift.external_snapshot_synced_at, timezone)}`
                                : '—'}
                        </p>
                    </div>
                    {userIsAdmin && (
                        <React.Fragment>
                            <div>
                                <p className="text-muted-foreground text-sm font-medium">
                                    External ID
                                </p>
                                <p className="font-mono text-sm">
                                    {selectedShift.external_id || '—'}
                                </p>
                            </div>
                            <div>
                                <p className="text-muted-foreground text-sm font-medium">
                                    Frequency
                                </p>
                                <p className="text-sm">
                                    {selectedShift.frequency.toUpperCase()}
                                </p>
                            </div>
                            {userIsAdmin && organizationId && (
                                <ClientSection
                                    organizationId={organizationId}
                                    shiftId={selectedShift.id}
                                    currentClientId={localClientId}
                                    onClientChange={handleClientChange}
                                    onClientUpdated={
                                        onShiftUpdated ?? (() => {})
                                    }
                                    clientReferenceTerm={clientReferenceTerm}
                                    clientReferenceTermTitle={
                                        clientReferenceTermTitle
                                    }
                                />
                            )}
                            {userIsAdmin && organizationId && (
                                <CareCoordinatorSection
                                    organizationId={organizationId}
                                    clientId={localClientId}
                                    allCareCoordinators={allCareCoordinators}
                                    clientReferenceTerm={clientReferenceTerm}
                                />
                            )}
                        </React.Fragment>
                    )}
                </div>
                {selectedShift.patient_care_plan && (
                    <React.Fragment>
                        <h3 className="mt-6 border-t pt-4 text-lg font-semibold">
                            Care Plan
                        </h3>
                        <div className="mt-4 rounded-lg border p-4">
                            <Markdown className="text-sm">
                                {selectedShift.patient_care_plan}
                            </Markdown>
                        </div>
                    </React.Fragment>
                )}
            </div>
            <div className="flex flex-col overflow-hidden border-t pt-4 md:col-span-3 md:h-full md:border-t-0 md:border-l md:pt-0 md:pl-6">
                <h3 className="mb-2 text-lg font-semibold">Message History</h3>
                <div className="flex min-h-[300px] flex-1 flex-col overflow-hidden md:min-h-0">
                    {selectedShift.caregiver && (
                        <MessagesPane
                            timezone={timezone}
                            organizationId={organizationId}
                            domain="shift_confirmation"
                            domainId={selectedShift.latest_confirmation_id}
                            conversationId={selectedShift.conversation_id}
                            refreshOnOpen={true}
                            showComposer={true}
                        />
                    )}
                    {!selectedShift.caregiver && (
                        <EmptyState
                            className="flex-1"
                            title="No caregiver conversation yet"
                            description="This shift is unassigned, so there’s no caregiver conversation yet. Caregiver replies for an open shift are reviewed on its outreach."
                            action={
                                activeCallout ? (
                                    <Button
                                        variant="outline"
                                        onClick={() =>
                                            onNavigateToOutreach(
                                                activeCallout.id,
                                            )
                                        }
                                    >
                                        View outreach
                                    </Button>
                                ) : undefined
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    ) : null;

    const shiftHeader = selectedShift ? (
        <DialogHeader className="flex flex-row items-center justify-between gap-4 pr-10">
            <div className="min-w-0">
                <DialogTitle className="truncate text-lg font-semibold">
                    {selectedShift.patient_name || 'Shift Details'}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground mt-0.5 text-sm">
                    {formatDayOfWeek(selectedShift.shift_start_time, timezone)}
                    {', '}
                    {formatFullDate(selectedShift.shift_start_time, timezone)}
                    {' \u00B7 '}
                    {formatTime(selectedShift.shift_start_time, timezone)}
                    {' \u2013 '}
                    {formatTime(selectedShift.shift_end_time, timezone)}
                </DialogDescription>
            </div>
            {headerButtons}
        </DialogHeader>
    ) : null;

    const content =
        selectedShift && organizationId ? (
            <Tabs
                defaultValue="details"
                className="flex min-h-0 flex-1 flex-col"
            >
                <TabsList className="self-start">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="activity">
                        <Activity className="h-4 w-4" />
                        Activity
                    </TabsTrigger>
                </TabsList>
                <TabsContent
                    value="details"
                    className="mt-2 flex min-h-0 flex-1 flex-col overflow-hidden"
                >
                    {detailsGrid}
                </TabsContent>
                <TabsContent
                    value="activity"
                    className="mt-2 min-h-0 flex-1 overflow-y-auto"
                >
                    <EntityEventsTimeline
                        organizationId={organizationId}
                        entityType="shift"
                        entityId={selectedShift.id}
                    />
                </TabsContent>
            </Tabs>
        ) : (
            detailsGrid
        );

    if (isMobile) {
        return (
            <Sheet open={isOpen} onOpenChange={handleOpenChange}>
                <SheetContent
                    side="right"
                    className="w-full overflow-y-auto p-4"
                >
                    <SheetHeader className="flex flex-row items-center justify-between gap-2">
                        <SheetTitle>
                            {selectedShift?.patient_name || 'Shift Details'}
                        </SheetTitle>
                        {headerButtons}
                    </SheetHeader>
                    {content}
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="flex h-[85vh] w-[65vw] max-w-[70vw] min-w-[800px] flex-col p-6 sm:max-w-[70vw]">
                {shiftHeader}
                {content}
            </DialogContent>
        </Dialog>
    );
}

type ShiftPageDetailsDialogProps = {
    readonly organizationId: string;
    readonly shiftId: string | null;
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onBeforeNavigateToOutreach?: () => void;
};

export function ShiftPageDetailsDialog({
    organizationId,
    shiftId,
    isOpen,
    onClose,
    onBeforeNavigateToOutreach,
}: ShiftPageDetailsDialogProps) {
    const navigate = useNavigate();
    const { timezone } = useOrganizationTimezone();
    const mode = useMode();
    const { data: shift, mutate: mutateShift } = useShift(
        organizationId,
        isOpen ? shiftId : null,
        {
            reason: 'shift-page-details-dialog',
        },
    );
    const { data: organization } = useOrganization(organizationId, {
        reason: 'shift-page-details-dialog',
    });
    const { data: caregivers } = useOrganizationCaregivers(organizationId, {
        reason: 'shift-page-details-dialog',
    });
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        {
            reason: 'shift-page-details-dialog',
        },
    );
    const { data: user } = useUser({
        reason: 'shift-page-details-dialog',
    });
    const caregiverForm = useForm<{ caregiver: string }>({
        defaultValues: { caregiver: 'unassigned' },
    });
    const [isSyncingSelectedShift, setIsSyncingSelectedShift] = useState(false);
    const [showCreateOutreachModal, setShowCreateOutreachModal] =
        useState(false);
    const [selectedShiftIdsForOutreach, setSelectedShiftIdsForOutreach] =
        useState<Set<string>>(new Set());
    const hasWellSkyAccount =
        dataSourceAccounts?.some((account) => account.type === 'wellsky') ??
        false;
    const hasSyncEnabledAccountForShift =
        dataSourceAccounts?.some(
            (account) =>
                (account.type === 'wellsky' ||
                    account.type === 'axiscare' ||
                    account.type === 'hhaexchange') &&
                account.sync_enabled,
        ) ?? false;
    const clockInOutEnabled =
        !!organization?.features?.['clock_in_out_processing_enabled'];
    const outreachTypesToFetch = useMemo((): Array<OutreachType> => {
        if (clockInOutEnabled) {
            return ['callout', 'recurring_shift', 'clock_in', 'clock_out'];
        }
        return ['callout', 'recurring_shift'];
    }, [clockInOutEnabled]);
    const shiftIds = useMemo(
        () => (isOpen && shiftId ? [shiftId] : []),
        [isOpen, shiftId],
    );
    const { data: outreaches } = useOutreachesBatch(
        organizationId,
        shiftIds,
        { reason: 'shift-page-details-dialog' },
        { outreachTypes: outreachTypesToFetch },
    );
    const selectedShiftCalloutStatus = useMemo(() => {
        if (!shiftId) {
            return EMPTY_SHIFT_CALLOUT_STATUS;
        }

        return getShiftCalloutStatus({
            shiftId,
            outreaches,
        });
    }, [outreaches, shiftId]);
    const displayCaregiverNamesLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;
    const clientReferenceTerm =
        organization?.general_settings?.client_reference_term ?? 'client';
    const clientReferenceTermTitle =
        formatClientReferenceTerm(clientReferenceTerm);
    const selectedShiftIdsForOutreachArray = useMemo(
        () =>
            selectedShiftIdsForOutreach.size > 0
                ? [...selectedShiftIdsForOutreach]
                : undefined,
        [selectedShiftIdsForOutreach],
    );

    const handleCreateCallout = useCallback((selectedShift: Shift) => {
        setSelectedShiftIdsForOutreach(new Set([selectedShift.id]));
        setShowCreateOutreachModal(true);
    }, []);

    const handleSyncSelectedShift = useCallback(async () => {
        if (!shift) {
            return;
        }

        const organizationIdForShift = shift.organization_id ?? organizationId;

        if (!organizationIdForShift) {
            toast.error('Organization ID not found', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        if (!hasSyncEnabledAccountForShift) {
            toast.error('Syncing is not enabled for this organization.', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        setIsSyncingSelectedShift(true);

        let syncSucceeded = false;
        try {
            await getApiClient().post({
                url: '/organizations/{organization_id}/shifts/{shift_id}/sync',
                pathParams: {
                    organization_id: organizationIdForShift,
                    shift_id: shift.id,
                },
            });
            syncSucceeded = true;
        } catch (error) {
            console.error('Error triggering shift sync:', error);
            if (error instanceof ApiError && error.status === 429) {
                toast.error(
                    'Rate limit exceeded. Please try again in a few minutes.',
                    {
                        persistNotification: true,
                        notificationType: 'shift_action',
                    },
                );
            } else {
                toast.error('Failed to start shift sync. Please try again.', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            }
        }

        if (syncSucceeded) {
            toast.success('Shift synced', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            try {
                await mutateShift();
            } catch (refreshError) {
                console.error(
                    'Failed to refresh shift after sync:',
                    refreshError,
                );
            }
        }

        setIsSyncingSelectedShift(false);
    }, [shift, organizationId, hasSyncEnabledAccountForShift, mutateShift]);

    const handleSetCaregiver = useCallback(
        async (selectedShift: Shift, caregiverId: string, ehrName?: string) => {
            try {
                const updateData: { assigned_caregiver_id?: string | null } =
                    {};
                updateData.assigned_caregiver_id =
                    caregiverId === 'unassigned' ? null : caregiverId;

                const updateResponse = await getApiClient().patch({
                    url: '/organizations/{organization_id}/shifts/{shift_id}',
                    pathParams: {
                        organization_id: organizationId,
                        shift_id: selectedShift.id,
                    },
                    body: updateData,
                });

                await mutateShift();
                showCaregiverWritebackToast(
                    caregiverId,
                    updateResponse,
                    ehrName,
                );
            } catch (error) {
                console.error('Error updating caregiver assignment:', error);
                toast.error('Failed to update caregiver assignment', {
                    persistNotification: true,
                    notificationType: 'shift_action',
                });
            }
        },
        [organizationId, mutateShift],
    );

    return (
        <React.Fragment>
            <ShiftDetailsDialog
                selectedShift={isOpen ? (shift ?? null) : null}
                selectedShiftDetails={isOpen ? (shift ?? null) : null}
                timezone={timezone}
                displayCaregiverNamesLastFirst={displayCaregiverNamesLastFirst}
                caregiverUnassignmentEnabled={isEhrCaregiverUnassignmentWritebackEnabled(
                    organization?.features,
                )}
                caregivers={caregivers}
                caregiverForm={caregiverForm}
                mode={mode}
                organizationId={organizationId}
                hasActiveCallout={selectedShiftCalloutStatus.hasActiveCallout}
                activeCallout={selectedShiftCalloutStatus.activeCallout}
                activeClockReminder={
                    selectedShiftCalloutStatus.activeClockReminder
                }
                hasWellSkyAccount={hasWellSkyAccount}
                hasSyncEnabledAccountForShift={hasSyncEnabledAccountForShift}
                dataSourceAccounts={dataSourceAccounts}
                onSyncSelectedShift={handleSyncSelectedShift}
                isSyncingSelectedShift={isSyncingSelectedShift}
                onCreateCallout={handleCreateCallout}
                onSetCaregiver={handleSetCaregiver}
                onClose={onClose}
                onNavigateToOutreach={(outreachId) => {
                    onClose();
                    onBeforeNavigateToOutreach?.();
                    void navigate({
                        to: '/outreaches/$callout-id',
                        params: { 'callout-id': outreachId },
                    });
                }}
                userIsAdmin={Boolean(user?.admin)}
                onShiftUpdated={() => {
                    void mutateShift();
                }}
                readOnly={!user?.admin}
                openedFromCaregiverModal={true}
                clientReferenceTerm={clientReferenceTerm}
                clientReferenceTermTitle={clientReferenceTermTitle}
            />
            <CreateOutreachModal
                isOpen={showCreateOutreachModal}
                shiftIds={selectedShiftIdsForOutreachArray}
                onClose={() => {
                    setShowCreateOutreachModal(false);
                    setSelectedShiftIdsForOutreach(new Set());
                }}
                onSuccess={() => {
                    setShowCreateOutreachModal(false);
                    setSelectedShiftIdsForOutreach(new Set());
                }}
            />
        </React.Fragment>
    );
}

type SendConfirmationsDialogProps = {
    readonly isOpen: boolean;
    readonly isSending: boolean;
    readonly sendPayload: SendPayload | null;
    readonly selectedDateRange: DateRangePickerValue;
    readonly timezone: string;
    readonly onClose: () => void;
    readonly onConfirm: (payload: SendPayload) => void;
};

function SendConfirmationsDialog({
    isOpen,
    isSending,
    sendPayload,
    selectedDateRange,
    timezone,
    onClose,
    onConfirm,
}: SendConfirmationsDialogProps) {
    const isSingleDay = selectedDateRange.from === selectedDateRange.to;
    const dateRangeLabel = isSingleDay
        ? 'on ' + formatDateKey(selectedDateRange.from, timezone)
        : `from ${formatDateKey(selectedDateRange.from, timezone)} to ${formatDateKey(selectedDateRange.to, timezone)}`;

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Send Confirmations</DialogTitle>
                    <DialogDescription>
                        {!sendPayload ? (
                            <span>
                                No assigned shifts match your current filters.
                            </span>
                        ) : sendPayload.mode === 'ids' ? (
                            <span>
                                You are about to send confirmation messages to{' '}
                                <strong>{sendPayload.ids.length}</strong>{' '}
                                caregiver
                                {sendPayload.ids.length === 1 ? '' : 's'} for
                                shifts {dateRangeLabel}.
                            </span>
                        ) : (
                            <span>
                                You are about to send confirmation messages for
                                shifts {dateRangeLabel}.
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (!sendPayload) {
                                return;
                            }
                            onConfirm(sendPayload);
                        }}
                        disabled={
                            isSending ||
                            !sendPayload ||
                            (sendPayload.mode === 'ids' &&
                                sendPayload.ids.length === 0)
                        }
                    >
                        {isSending ? 'Sending...' : 'Send Confirmations'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
